// La ficha de un producto dentro del lanzamiento — ADR-2026-09-06-lanzamientos-ficha-completa.
//
// Son todos los campos que Tiendanube acepta al crear un producto, y son muchos,
// así que van en secciones plegables: abiertas las dos que casi siempre se tocan
// (lo básico y el precio) y cerradas las demás, que se abren solas si ya tienen algo
// cargado. Un producto con nombre y precio se publica sin abrir ninguna.
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { pasosDeLaFicha, useRecorrido } from './recorridos';
import {
  AlertCircle,
  ArrowLeftRight,
  ChevronDown,
  ChevronUp,
  FolderTree,
  ImagePlus,
  HelpCircle,
  Images,
  Layers,
  Loader2,
  Package,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Settings2,
  Tag,
  Truck,
  ShoppingBag,
  Check,
  Eye,
  ExternalLink,
  Wallet,
  X,
} from 'lucide-react';
import SortableImageList from '../../components/tnexport/SortableImageList';
import { Seccion } from './Seccion';
import { TarjetaDeEstado } from './TarjetaDeEstado';
import { EnTuTienda, type LecturaDeLaTienda } from './EnTuTienda';
import { cambiosContraLaTienda, type CambioDeCampo } from './cambiosContraLaTienda';
import { traerLoDeLaTienda } from './traerDeLaTienda';
import { ConRotulo } from '../../components/ConRotulo';
import { BotonMejorarConIa, PropuestaDeIa, useMejorarConIa } from './MejorarConIa';
import {
  conDestrabadas,
  estaTrabada,
  resumenEnLaTienda,
  SECCIONES_TRABABLES,
  TITULO_DE_SECCION,
  type SeccionTrabable,
} from './seccionesTrabadas';
import { SeccionMercadoLibre } from './SeccionMercadoLibre';
import type { DatosMlDeFicha } from '../../lib/mercadoLibrePublicarLogica';
import { AgregarFoto } from './AgregarFoto';
import { SelectorDeCreaciones } from './SelectorDeCreaciones';
import { aNumero, margenDeGanancia, problemasDeFicha } from './fichaTiendanube';
// Lo que exige cada destino (topes, una categoría, paquete obligatorio) vive
// en un solo lugar y la ficha solo lo lee: ADR-2026-09-13-lanzamientos-para-vtex.
import {
  ayudaDeCategorias,
  ayudaDeEnvio,
  hayConfiguracionDeVariantes,
  reglasDeDestinos,
  separaImagenesPorVariante,
  type CanalLanzamiento,
  type ReglasDelCanal,
} from './canales';
import {
  actualizarFicha,
  actualizarVariante,
  agregarVariante,
  asignarImagenAVariante,
  asignarSueltasAVariante,
  subirImagenAProducto,
  sumarGeneradasAProducto,
  fichaDe,
  firmarFull,
  guardarSeccionesDestrabadas,
  marcarCategoriaDeProducto,
  reemplazarCategoriaDeProducto,
  MAX_IMAGENES_POR_PRODUCTO,
  quitarVariante,
  reordenarVariantes,
  type CamposDeFicha,
  type ProductoDeLanzamiento,
  type VarianteDeLanzamiento,
} from './lanzamientosApi';
import { atributosSugeridos } from './atributosSugeridos';
import { useReordenAnimado } from '../../hooks/useReordenAnimado';
import { fotosDeVariante, moverUnEscalon, reordenarDentroDelGrupo } from './ordenDeFotos';
// El nombre de una variante es el MISMO acá y en el desglose de la tabla: si en
// un lado dijera "Rojo / M" y en el otro "Variante 2", no se podría saber que
// son la misma.
import { etiquetaDeVariante } from './variantes';
import { SelectorDeCategorias } from './Categorias';
import type { CategoriaDeTienda } from './lanzamientosApi';
import PopoverAnclado from './PopoverAnclado';
import { ModalConfirmar } from '../../components/ModalConfirmar';
import { TiendaNubeIcon } from '../../components/TiendaNubeIcon';
import { guardarFotosABorrar } from './lanzamientosApi';
import { describirPublicacionDeMl } from '../../lib/mercadoLibreFotos';
import {
  camposDesdeLaTienda,
  GENEROS,
  RANGOS_DE_EDAD,
  VISIBILIDADES,
  type Genero,
  type RangoDeEdad,
} from './camposDeLaTienda';
import VisorDeFoto from './VisorDeFoto';
import { colorDeTiendanube, esAtributoColor } from './coloresTiendanube';
import EditorDePropiedad from './EditorDePropiedad';
import { planDeVariantes, propiedadesDe, type Propiedad } from './propiedades';

/** Los textos de la ficha que lee el pedido de "Mejorar con IA". */
const TEXTOS_DEL_PEDIDO = ['nombre', 'descripcion', 'marca', 'seoTitulo', 'seoDescripcion'] as const;
type TextoDelPedido = (typeof TEXTOS_DEL_PEDIDO)[number];

// ── Piezas de formulario ───────────────────────────────────────────────────

/** Campo que guarda al salir del foco, como el resto de la configuración. */
function Campo({
  etiqueta,
  valor,
  onGuardar,
  tipo = 'text',
  placeholder,
  multilinea,
  requerido,
  sufijo,
  ayuda,
  maxLength,
  cambio,
  onDeshacer,
  accion,
}: {
  etiqueta: string;
  valor: string;
  onGuardar: (v: string) => void;
  tipo?: string;
  placeholder?: string;
  multilinea?: boolean;
  requerido?: boolean;
  sufijo?: string;
  ayuda?: string;
  maxLength?: number;
  /** Distinto de lo que está publicado en la tienda: se marca en azul con lo de allá. */
  cambio?: CambioDeCampo | null;
  /** Vuelve el campo a lo de la tienda. */
  onDeshacer?: () => void;
  /** Un botón chico al lado de la etiqueta ("Mejorar con IA"). */
  accion?: ReactNode;
}) {
  const [local, setLocal] = useState(valor);
  useEffect(() => setLocal(valor), [valor]);
  const excedido = maxLength !== undefined && local.length > maxLength;
  // Un obligatorio vacío en ámbar, como el resto de lo que falta en la ficha: es trabajo
  // pendiente, no un error (ADR-2026-10-06-la-ficha-ocupa-la-pantalla). Un cambio contra la tienda
  // en azul: no falta nada, es lo que se va a actualizar
  // (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento).
  const borde =
    requerido && !local.trim()
      ? 'border-amber-300 bg-amber-50/40'
      : excedido
        ? 'border-amber-400'
        : cambio
          ? 'border-[rgb(var(--brand-primary-rgb)_/_0.4)] bg-[rgb(var(--brand-primary-rgb)_/_0.035)]'
          : 'border-neutral-200';
  const comun = `mt-1 w-full rounded-xl border ${borde} py-2.5 px-4 text-sm text-brand-black outline-none transition-shadow placeholder:text-neutral-400 focus:border-brand-primary focus:bg-white focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.1)]`;
  // "Deshacer" va adentro del campo, a la derecha y sólo con el ícono: el nombre sale al pasar el
  // mouse, con el mismo rótulo que las acciones de las capas
  // (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas).
  const deshacer = cambio && onDeshacer ? (
    <ConRotulo rotulo="Deshacer">
      <button
        type="button"
        onClick={onDeshacer}
        aria-label={`Deshacer: ${etiqueta.toLowerCase()} vuelve a lo de tu tienda`}
        className="grid h-7 w-7 place-items-center rounded-full text-brand-primary transition-colors hover:bg-[rgb(var(--brand-primary-rgb)_/_0.125)]"
      >
        <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </ConRotulo>
  ) : null;
  const relleno = deshacer && sufijo ? 'pr-20' : deshacer ? 'pr-11' : sufijo ? 'pr-12' : '';
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-2">
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-xs font-medium text-neutral-500">
            {etiqueta}
            {requerido && <span className="text-red-500"> *</span>}
          </span>
          {accion}
        </span>
        {maxLength !== undefined && (
          <span className={`shrink-0 text-[11px] ${excedido ? 'text-amber-600' : 'text-neutral-400'}`}>
            {local.length}/{maxLength}
          </span>
        )}
      </span>
      <span className="relative block">
        {multilinea ? (
          <textarea
            rows={4}
            value={local}
            placeholder={placeholder}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => local !== valor && onGuardar(local)}
            className={`${comun} resize-y ${deshacer ? 'pr-11' : ''}`}
          />
        ) : (
          <input
            type={tipo}
            inputMode={tipo === 'number' ? 'decimal' : undefined}
            value={local}
            placeholder={placeholder}
            onChange={(e) => setLocal(e.target.value)}
            onBlur={() => local !== valor && onGuardar(local)}
            className={`${comun} ${relleno}`}
          />
        )}
        {deshacer ? (
          multilinea ? (
            <span className="absolute right-2 top-3">{deshacer}</span>
          ) : (
            // `top-1`: el campo tiene `mt-1`, y así queda centrado sobre el campo y no sobre el margen.
            <span className="absolute bottom-0 right-2 top-1 flex items-center gap-1">
              {deshacer}
              {sufijo && <span className="pointer-events-none pr-2 text-xs text-neutral-400">{sufijo}</span>}
            </span>
          )
        ) : (
          sufijo && (
            <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-neutral-400">
              {sufijo}
            </span>
          )
        )}
      </span>
      {cambio && (
        <span className="mt-1 block text-[11px] text-brand-primary">
          Valor actual en tu tienda:{' '}
          <span className="text-brand-black">{cambio.antes.length > 80 ? `${cambio.antes.slice(0, 79)}…` : cambio.antes}</span>
        </span>
      )}
      {ayuda && <span className="mt-1 block text-[11px] text-neutral-400">{ayuda}</span>}
    </label>
  );
}

/**
 * Cada sección de la ficha es una card blanca.
 *
 * El panel de contenido de escritorio ya es casi blanco (`--app-content-bg`), así
 * que lo que separa una card del fondo es el borde más una sombra suave, no un
 * cambio de color: sobre este fondo, blanco contra blanco no se ve.
 *
 * Los íconos van todos en el MISMO gris a propósito. El color en esta pantalla
 * significa estado —falta algo, el margen es negativo, hubo un error— y si además
 * pintara cada sección de un tono distinto dejaría de querer decir nada.
 */
/**
 * Campo sin etiqueta para las filas de variantes: la etiqueta va UNA vez en el
 * encabezado de la lista, como en una tabla, en vez de repetirse en cada fila.
 */
function CampoEnFila({
  etiqueta,
  valor,
  onGuardar,
  tipo = 'text',
}: {
  etiqueta: string;
  valor: string;
  onGuardar: (v: string) => void;
  tipo?: string;
}) {
  const [local, setLocal] = useState(valor);
  useEffect(() => setLocal(valor), [valor]);
  return (
    <label className="block min-w-0">
      <span className="mb-0.5 block text-[11px] text-neutral-500 sm:sr-only">{etiqueta}</span>
      <input
        type={tipo}
        inputMode={tipo === 'number' ? 'decimal' : undefined}
        value={local}
        placeholder="General"
        aria-label={etiqueta}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => local !== valor && onGuardar(local)}
        className="w-full rounded-lg border border-neutral-200 py-1.5 px-2.5 text-sm text-brand-black outline-none placeholder:text-neutral-400 focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.1)]"
      />
    </label>
  );
}

/** El margen es la lectura del costo: sin él, el costo es un número suelto. */
function Margen({ precio, costo }: { precio: string | null; costo: string | null }) {
  const m = margenDeGanancia(precio, costo);
  if (m === null) {
    return (
      <div className="rounded-xl bg-neutral-50 py-2.5 px-4">
        <p className="text-xs font-medium text-neutral-500">Margen</p>
        <p className="mt-0.5 text-sm text-neutral-400">Cargá el costo</p>
      </div>
    );
  }
  const perdida = m < 0;
  return (
    <div className={`rounded-xl py-2.5 px-4 ${perdida ? 'bg-red-50' : 'bg-green-50'}`}>
      <p className={`text-xs font-medium ${perdida ? 'text-red-700' : 'text-green-700'}`}>Margen</p>
      <p className={`mt-0.5 text-sm font-semibold ${perdida ? 'text-red-700' : 'text-green-700'}`}>
        {m.toFixed(1)}%
      </p>
    </div>
  );
}

// ── Variantes ──────────────────────────────────────────────────────────────

/** Interruptor de "esto lo define cada variante". */
function InterruptorPorVariante({
  titulo,
  detalle,
  activo,
  onCambiar,
}: {
  titulo: string;
  detalle: string;
  activo: boolean;
  onCambiar: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={activo}
      onClick={() => onCambiar(!activo)}
      className={`flex w-full items-start gap-3 border-t border-neutral-100 py-3 px-4 text-left transition-colors ${
        activo ? 'bg-neutral-50' : 'hover:bg-neutral-50/60'
      }`}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm text-brand-black">{titulo}</span>
        <span className="mt-0.5 block text-[11px] text-neutral-500">{detalle}</span>
      </span>
      <span
        className={`mt-0.5 shrink-0 relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
          activo ? 'bg-brand-primary' : 'bg-neutral-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
            activo ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </span>
    </button>
  );
}

/**
 * Card punteada para sumar una foto, al final de la tira. Al tocarla abre un menú
 * con los dos orígenes posibles: una imagen que Atlas ya generó, o un archivo del
 * dispositivo.
 *
 * Es punteada y no llena a propósito: dice "acá falta algo" en vez de competir
 * con las fotos reales, que son el contenido.
 */
/** Las flechas de orden de una variante. Apagadas en los extremos en vez de
 *  escondidas: que el botón esté siempre en el mismo lugar es lo que deja
 *  hacer varios clicks seguidos sin volver a apuntar. */
const BOTON_DE_ORDEN =
  'rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-brand-black ' +
  'disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent ' +
  'disabled:hover:text-neutral-400';

/**
 * Una tira de fotos con todo lo que se puede hacer con ellas: arrastrar para
 * reordenar, quitar, y la card punteada para sumar una.
 *
 * Es la MISMA tira en el producto y en cada variante. Que las fotos de una
 * variante se manejaran distinto que las del producto sería una segunda forma de
 * hacer lo mismo, y la primera ya está resuelta. Lo único que cambia es
 * `accionExtra` —el botón para mandar la foto a otra variante—, que en el
 * producto no tiene a dónde mandar nada.
 */
function TiraDeFotos({
  imagenes,
  sinLugar,
  subiendo,
  onReordenar,
  onQuitar,
  onElegirGenerada,
  onSubir,
  onVer,
  accionExtra,
}: {
  imagenes: ProductoDeLanzamiento['imagenes'];
  sinLugar: boolean;
  subiendo: boolean;
  onReordenar: (ids: string[]) => void;
  onQuitar: (id: string) => void;
  onElegirGenerada: () => void;
  onSubir: (archivo: File) => void;
  onVer: (id: string) => void;
  accionExtra?: (id: string) => React.ReactNode;
}) {
  // "Agregar" va ADENTRO de la grilla, como una celda más: afuera caía solo en un renglón
  // nuevo. En el teléfono, tres por fila (ADR-2026-10-16-el-detalle-del-lanzamiento-en-el-telefono).
  return (
    <SortableImageList
      grande
      tresPorFila
      imagenes={imagenes.map((i) => ({ id: i.id, orden: i.orden, thumbUrl: i.thumbUrl }))}
      onReorder={onReordenar}
      onRemove={onQuitar}
      onVer={onVer}
      accionExtra={accionExtra}
      alFinal={
        <AgregarFoto
          tresPorFila
          subiendo={subiendo}
          deshabilitada={sinLugar}
          onElegirGenerada={onElegirGenerada}
          onSubir={onSubir}
        />
      }
    />
  );
}

/**
 * Botón de la esquina de una foto para mandarla a otra variante.
 *
 * Es la única diferencia entre la tira de una variante y la del producto: una
 * foto puede haber caído en la variante equivocada —todas arrancan en la
 * primera— y arrastrarla de una tira a otra no alcanza, porque cada tira es su
 * propio contexto de arrastre.
 */
function MoverAVariante({
  destinos,
  onMover,
}: {
  destinos: Array<{ id: string; etiqueta: string }>;
  onMover: (varianteId: string) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const botonRef = useRef<HTMLButtonElement | null>(null);

  // Con una sola variante no hay a dónde mover: el botón sería una promesa vacía.
  if (destinos.length === 0) return null;

  return (
    <>
      <button
        ref={botonRef}
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-label="Mover a otra variante"
        title="Mover a otra variante"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75"
      >
        <ArrowLeftRight className="w-3.5 h-3.5" />
      </button>
      <PopoverAnclado
        abierto={abierto}
        anclaRef={botonRef}
        onCerrar={() => setAbierto(false)}
        alineacion="derecha"
        ancho={224}
      >
        <p className="border-b border-neutral-100 py-2 px-3 text-[11px] text-neutral-500">
          Mover esta foto a…
        </p>
        {destinos.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => {
              setAbierto(false);
              onMover(d.id);
            }}
            className="block w-full truncate py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
          >
            {d.etiqueta}
          </button>
        ))}
      </PopoverAnclado>
    </>
  );
}

/**
 * Una variante. Muestra SIEMPRE sus valores de atributo más su stock y su SKU
 * —tener unidades y código propios es la razón de existir de una variante— y,
 * abajo, los campos de las dimensiones que el producto pasó a por-variante.
 *
 * Sin desplegables: si el usuario prendió "precio por variante", quiere ver el
 * precio, no un botón que lo esconde.
 */
function FilaDeVariante({
  nodoRef,
  variante,
  etiqueta,
  colorHex,
  reglas,
  porPrecio,
  porEnvio,
  porImagenes,
  fotos,
  sinLugar,
  subiendo,
  destinos,
  puedeSubir,
  puedeBajar,
  onGuardar,
  onQuitar,
  onMoverArriba,
  onMoverAbajo,
  onReordenarFotos,
  onQuitarFoto,
  onElegirGenerada,
  onSubirFoto,
  onMoverFoto,
  onVerFoto,
}: {
  /** El nodo de la card, para que el reorden pueda animarla de un lugar al otro. */
  nodoRef: (el: HTMLDivElement | null) => void;
  variante: VarianteDeLanzamiento;
  /** "S / Negro": los valores salen de las propiedades, no se escriben acá. */
  etiqueta: string;
  /** La muestra del color, si una propiedad es Color y el valor está en la lista. */
  colorHex: string | null;
  /** Qué exige el destino: decide si la variante muestra datos de venta. */
  reglas: ReglasDelCanal;
  porPrecio: boolean;
  porEnvio: boolean;
  porImagenes: boolean;
  /** Solo las de ESTA variante, en el orden del producto. */
  fotos: ProductoDeLanzamiento['imagenes'];
  sinLugar: boolean;
  subiendo: boolean;
  /** Las otras variantes, para el botón de mover. */
  destinos: Array<{ id: string; etiqueta: string }>;
  /** La primera no puede subir y la última no puede bajar. */
  puedeSubir: boolean;
  puedeBajar: boolean;
  onGuardar: (campos: Partial<VarianteDeLanzamiento>) => void;
  onQuitar: () => void;
  onMoverArriba: () => void;
  onMoverAbajo: () => void;
  onReordenarFotos: (ids: string[]) => void;
  onQuitarFoto: (id: string) => void;
  onElegirGenerada: () => void;
  onSubirFoto: (archivo: File) => void;
  onMoverFoto: (imagenId: string, varianteId: string) => void;
  onVerFoto: (id: string) => void;
}) {
  return (
    <div ref={nodoRef} className="rounded-xl border border-neutral-200 bg-surface-card py-2 px-3">
      {/* Una fila, como la tabla de Tienda Nube: la variante, su stock y su SKU, y las
          acciones. En el teléfono, stock y SKU bajan a una segunda línea. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:flex-nowrap">
        {/* Los valores de la variante salen de las propiedades (Talle: S, M…): acá se
            leen, no se escriben. Cambiar un talle es cambiarlo en la propiedad, y
            así no quedan dos variantes iguales ni una a medio cargar
            (ADR-2026-10-06-variantes-por-propiedades). */}
        <p className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-brand-black">
          {colorHex && (
            <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-black/10" style={{ backgroundColor: colorHex }} aria-hidden="true" />
          )}
          <span className="truncate">{etiqueta}</span>
        </p>
        {/* Stock y SKU van siempre que el destino VENDA: son propios de cada
            variante por definición. Exportando a carpetas no existen. */}
        {reglas.conPrecioYStock && (
          <div className="order-last grid w-full grid-cols-2 gap-2 sm:order-none sm:w-auto sm:grid-cols-[112px_160px]">
            <CampoEnFila etiqueta="Stock" tipo="number" valor={variante.stock ?? ''} onGuardar={(v) => onGuardar({ stock: v })} />
            <CampoEnFila etiqueta="SKU" valor={variante.sku ?? ''} onGuardar={(v) => onGuardar({ sku: v })} />
          </div>
        )}
        {/* Subir, bajar y quitar. El orden de las variantes es el que Tiendanube
            muestra en el selector de la tienda, así que moverlas acá cambia lo
            que ve el comprador. */}
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            onClick={onMoverArriba}
            disabled={!puedeSubir}
            aria-label="Subir la variante una posición"
            title="Subir una posición"
            className={BOTON_DE_ORDEN}
          >
            <ChevronUp className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onMoverAbajo}
            disabled={!puedeBajar}
            aria-label="Bajar la variante una posición"
            title="Bajar una posición"
            className={BOTON_DE_ORDEN}
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={onQuitar}
            aria-label="Quitar variante"
            title="Quitar variante"
            className="rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>


      {porPrecio && (
        <div className="mt-2 grid gap-2 sm:grid-cols-4">
          <Campo
            etiqueta="Precio"
            tipo="number"
            valor={variante.precio ?? ''}
            placeholder="Usa el general"
            onGuardar={(v) => onGuardar({ precio: v })}
          />
          <Campo
            etiqueta="Costo"
            tipo="number"
            valor={variante.costo ?? ''}
            placeholder="Usa el general"
            onGuardar={(v) => onGuardar({ costo: v })}
          />
          <Campo
            etiqueta="Código de barras"
            valor={variante.codigoBarras ?? ''}
            placeholder="Usa el general"
            onGuardar={(v) => onGuardar({ codigoBarras: v })}
          />
          <Margen precio={variante.precio} costo={variante.costo} />
        </div>
      )}

      {porEnvio && (
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Campo etiqueta="Peso" tipo="number" sufijo="kg" placeholder="General" valor={variante.peso ?? ''} onGuardar={(v) => onGuardar({ peso: v })} />
          <Campo etiqueta="Alto" tipo="number" sufijo="cm" placeholder="General" valor={variante.alto ?? ''} onGuardar={(v) => onGuardar({ alto: v })} />
          <Campo etiqueta="Ancho" tipo="number" sufijo="cm" placeholder="General" valor={variante.ancho ?? ''} onGuardar={(v) => onGuardar({ ancho: v })} />
          <Campo etiqueta="Prof." tipo="number" sufijo="cm" placeholder="General" valor={variante.profundidad ?? ''} onGuardar={(v) => onGuardar({ profundidad: v })} />
        </div>
      )}

      {/* Las fotos de la variante, con la MISMA tira que el producto. La línea
          de arriba las separa de los campos: son otra clase de contenido. */}
      {porImagenes && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <p className="text-xs font-medium text-neutral-500">Fotos de esta variante</p>
          <div className="mt-2">
            <TiraDeFotos
              imagenes={fotos}
              sinLugar={sinLugar}
              subiendo={subiendo}
              onReordenar={onReordenarFotos}
              onQuitar={onQuitarFoto}
              onElegirGenerada={onElegirGenerada}
              onSubir={onSubirFoto}
              onVer={onVerFoto}
              accionExtra={(id) => (
                <MoverAVariante destinos={destinos} onMover={(v) => onMoverFoto(id, v)} />
              )}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-neutral-400">
            {reglas.salida === 'zip'
              ? fotos.length === 0
                ? 'Sin fotos, esta variante no genera carpeta en el ZIP.'
                : 'Van a su propia carpeta, numeradas desde 1.'
              : fotos.length === 0
                ? 'Sin fotos propias, en la tienda esta variante muestra la portada del producto.'
                : 'La primera es la que Tiendanube muestra al elegir esta variante. Todas van a la galería.'}
          </p>
        </div>
      )}
    </div>
  );
}

/** Clave de "la tira del producto" cuando hay que distinguirla de las de las
 *  variantes, que se identifican por su id. */
const PRODUCTO = 'producto';

/**
 * Lo que cambia contra la tienda en una sección sin campo propio para marcarlo (las variantes, las
 * categorías): una línea por cambio, con el valor de allá en negro, como debajo de un campo.
 */
function ListaDeCambios({ cambios }: { cambios: CambioDeCampo[] }) {
  if (cambios.length === 0) return null;
  return (
    <ul className="space-y-1 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.049)] py-2 px-3 text-[11px] text-brand-primary">
      {cambios.map((c) => (
        <li key={c.campo}>
          <span className="font-medium">{c.etiqueta}</span> · Valor actual en tu tienda:{' '}
          <span className="text-brand-black">{c.antes}</span> → <span className="text-brand-black">{c.ahora}</span>
        </li>
      ))}
    </ul>
  );
}

// ── La ficha ───────────────────────────────────────────────────────────────

export default function FichaDeProducto({
  producto,
  companyId,
  canal,
  destinos,
  brandId,
  rubro,
  onCambio,
  fotosSueltas = false,
  mercadoLibre = null,
  onDatosMl,
  onQuitarImagen,
  onReordenarImagenes,
  categorias,
  cargandoCategorias,
  onAbrirCategorias,
  comentariosPendientes,
  onAbrirComentarios,
  onCompletarConIa,
  lecturaDeLaTienda,
  onReleerTienda,
}: {
  producto: ProductoDeLanzamiento;
  companyId: string | null;
  /** A dónde va el lanzamiento: decide qué le exige la ficha al producto. */
  canal: CanalLanzamiento;
  /** Todas las tiendas del lanzamiento (ADR-2026-09-30-lanzamiento-a-varias-tiendas). Sin esto, `[canal]`. */
  destinos?: CanalLanzamiento[];
  /** La marca del lanzamiento: su cuenta de Mercado Libre (la sección de Mercado Libre la usa). */
  brandId?: string | null;
  /** Rubro de la marca activa: decide qué atributos se sugieren. */
  rubro: string | null;
  onCambio: () => Promise<void>;
  /**
   * Producto abierto desde Tienda online: sin el interruptor "Agregar / Reemplazar"; cada foto de la
   * tienda se borra por separado y las fotos nuevas se suman
   * (ADR-2026-10-29-fotos-de-la-tienda-se-borran-una-por-una).
   */
  fotosSueltas?: boolean;
  /**
   * Mercado Libre, la otra tienda del producto: Fotos se puede ver como está allá y elegir si los cambios
   * van también (ADR-2026-10-30-un-cambio-en-varias-tiendas). null = no se ofrece.
   */
  mercadoLibre?: VistaDeMl | null;
  /** Lo que guardó la sección de Mercado Libre: la pantalla actualiza su copia sin recargar. */
  onDatosMl?: (lanzamientoProductoId: string, datos: DatosMlDeFicha) => void;
  onQuitarImagen: (id: string) => Promise<{ error: string | null }>;
  onReordenarImagenes: (ids: string[]) => Promise<{ error: string | null }>;
  /** El árbol de la marca. Lo carga la pantalla: lo comparten todas las fichas. */
  categorias: CategoriaDeTienda[];
  cargandoCategorias: boolean;
  onAbrirCategorias: () => void;
  /** Cuántos comentarios sin resolver tiene. Va en el botón: es lo que hace que
   *  alguien lo abra sin que le avisen por afuera. */
  comentariosPendientes: number;
  onAbrirComentarios: () => void;
  /** Abre "Completar con IA" para ESTE producto. El panel y la llamada viven en
   *  la pantalla: son los mismos que usa la selección múltiple. */
  onCompletarConIa: () => void;
  /**
   * Lo que hoy está publicado, si el producto ya está en Tiendanube. `undefined` = no aplica (se crea,
   * o el lanzamiento no va a Tiendanube). Con esto la ficha se edita y marca qué cambia
   * (ADR-2026-10-22-editar-productos-de-la-tienda-desde-un-lanzamiento).
   */
  lecturaDeLaTienda?: LecturaDeLaTienda;
  onReleerTienda?: () => void;
}) {
  const [guardando, setGuardando] = useState(false);
  const [configAbierta, setConfigAbierta] = useState(false);
  /**
   * A qué tira se le está sumando una foto: `null` = ninguna, `varianteId` =
   * la de esa variante, `PRODUCTO` = la del producto. Antes era un booleano;
   * con las fotos repartidas hay que saber a dónde va la que se elija, y el
   * spinner de "subiendo" tiene que girar solo en la card que lo pidió.
   */
  const [destinoDeAlta, setDestinoDeAlta] = useState<string | null>(null);
  const [subiendoEn, setSubiendoEn] = useState<string | null>(null);
  const [errorFoto, setErrorFoto] = useState<string | null>(null);
  /** Foto abierta en grande. La URL se firma al abrir: las miniaturas usan la
   *  versión liviana y el visor muestra la completa. */
  const [verFoto, setVerFoto] = useState<{ id: string; url: string | null } | null>(null);
  // La ayuda de "agregar / reemplazar" abierta con un toque: en el teléfono no hay
  // hover, y sin esto el "?" no se podría leer nunca.
  const [ayudaDeAccion, setAyudaDeAccion] = useState<'add' | 'replace' | null>(null);
  // La primera vez que se abre una ficha (ADR-2026-10-01-recorridos-de-tienda-online).
  useRecorrido('tienda_tour_ficha', true, pasosDeLaFicha);

  /**
   * Las categorías del producto, aplicadas en pantalla antes de guardar.
   *
   * Tildar una casilla tiene que responder en el acto. Recargar el lanzamiento
   * en cada tilde volvería a firmar todas las miniaturas y se verían parpadear
   * las fotos por marcar "Remeras"
   * (ADR-2026-09-06-lanzamientos-reorden-optimista).
   */
  const [suyas, setSuyas] = useState<string[]>(producto.categorias);
  const firmaDeCategorias = [...producto.categorias].sort().join(',');
  useEffect(() => {
    setSuyas(producto.categorias);
    // Por firma y no por el array: cada refetch trae uno nuevo aunque las
    // categorías sean las mismas, y sincronizar de más pisaría el tilde
    // optimista antes de que termine el guardado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaDeCategorias]);

  /**
   * Copia local de las fotos.
   *
   * Reordenar y quitar se aplican ACÁ primero y se guardan en segundo plano, sin
   * recargar el lanzamiento. Antes, al soltar una miniatura se esperaba el UPDATE
   * y después un refetch que volvía a firmar TODAS las URLs: las `src` cambiaban,
   * el navegador recargaba cada imagen y se veía un parpadeo de casi un segundo.
   * Peor: hasta que el refetch volvía, la lista seguía con el orden viejo y la
   * miniatura pegaba un salto de vuelta a su lugar anterior.
   *
   * Se sincroniza cuando el producto trae otras fotos de verdad (otro producto,
   * una foto nueva), no en cada render.
   */
  const [imagenes, setImagenes] = useState(producto.imagenes);
  const firmaDeFotos = producto.imagenes.map((i) => i.id).join(",");
  useEffect(() => {
    setImagenes(producto.imagenes);
    // `firmaDeFotos` y no `producto.imagenes`: el array es nuevo en cada refetch
    // aunque las fotos sean las mismas, y sincronizar de más pisaría el orden
    // optimista antes de que el guardado termine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaDeFotos]);

  // El tope es del PRODUCTO aunque las fotos se muestren repartidas: es el que
  // acepta Tiendanube por producto, no por variante.
  const sinLugar = imagenes.length >= MAX_IMAGENES_POR_PRODUCTO;
  /** Las variantes del catálogo de las que salieron sus fotos: ordenan el selector. */
  const varianteIdsDelProducto = useMemo(
    () => [...new Set(imagenes.map((i) => i.varianteId).filter((v): v is string => !!v))],
    [imagenes],
  );
  const generadasYaUsadas = useMemo(
    () => imagenes.map((i) => i.imagenGeneradaId).filter((v): v is string => !!v),
    [imagenes],
  );

  const subirFoto = async (archivo: File, varianteId: string | null) => {
    if (!companyId) return;
    setErrorFoto(null);
    setSubiendoEn(varianteId ?? PRODUCTO);
    const r = await subirImagenAProducto(producto.id, companyId, archivo, varianteId);
    setSubiendoEn(null);
    if (r.error) {
      setErrorFoto(r.error);
      return;
    }
    await onCambio();
  };
  const tiendas = destinos && destinos.length > 0 ? destinos : [canal];
  // Con varias tiendas, gana la regla más exigente de cada una.
  const reglas = reglasDeDestinos(tiendas);
  // `tn_product_id` es de Tiendanube: para cualquier otro destino (Mercado Libre incluido) el
  // producto es nuevo aunque ya viva en la tienda de Tiendanube, y la ficha entera cuenta.
  const seCrea = tiendas.some((d) => d !== 'tiendanube') || producto.tnProductId == null;
  // Ya está en la tienda: la ficha entera se ve y se edita, y lo que difiere de lo publicado se marca.
  const seEdita = lecturaDeLaTienda !== undefined;
  const muestraLaFicha = seCrea || seEdita;
  /**
   * Las secciones que se mandan a la tienda, aplicadas en pantalla antes de guardar: destrabar tiene
   * que abrir la sección en el acto (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas).
   * null = nada trabado (fila de antes, o sin la migración).
   */
  const [destrabadas, setDestrabadas] = useState<string[] | null>(producto.seccionesDestrabadas ?? null);
  const firmaDeDestrabadas = producto.seccionesDestrabadas == null ? 'null' : [...producto.seccionesDestrabadas].sort().join(',');
  useEffect(() => {
    setDestrabadas(producto.seccionesDestrabadas ?? null);
    // Por firma, como las categorías: cada refetch trae un array nuevo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaDeDestrabadas]);
  const comparacion = useMemo(
    () =>
      lecturaDeLaTienda?.estado === 'listo'
        ? cambiosContraLaTienda({ ...producto, seccionesDestrabadas: destrabadas }, lecturaDeLaTienda.publicado, categorias)
        : null,
    [lecturaDeLaTienda, producto, categorias, destrabadas],
  );
  /** Las trabas se ofrecen sólo con lo de la tienda leído: el resumen de una trabada sale de ahí. */
  const publicado = lecturaDeLaTienda?.estado === 'listo' ? lecturaDeLaTienda.publicado : null;
  const cambiarTraba = (seccion: SeccionTrabable, trabar: boolean) => {
    const antes = destrabadas;
    const nuevas = trabar
      ? (antes ?? []).filter((s) => s !== seccion)
      : conDestrabadas(antes, [seccion]);
    setDestrabadas(nuevas);
    void (async () => {
      const r = await guardarSeccionesDestrabadas(producto.id, nuevas);
      if (r.error) {
        setDestrabadas(antes);
        setErrorFoto(r.error);
        return;
      }
      // La fila de la tabla cuenta los cambios con lo guardado.
      await onCambio();
    })();
  };
  /**
   * "Mejorar con IA" de la descripción y el SEO: sólo en un producto que ya está en la tienda, que no
   * tiene "Completar con IA" (ADR-2026-10-24-mejorar-con-ia-la-descripcion-y-el-seo). Es el mismo pedido
   * que el panel, con lo que la ficha tiene hoy.
   */
  const mejora = !seCrea && publicado !== null;
  // Lo que se mandó a guardar de los textos que lee el pedido, hasta que vuelve en `producto`. El
  // campo guarda al perder el foco, que pasa en el mousedown de "Mejorar con IA": el click armaba el
  // pedido antes de que llegara el guardado y la IA mejoraba el texto anterior (review #201).
  const textosEnCamino = useRef<Partial<Record<TextoDelPedido, string | null>>>({});
  useEffect(() => {
    const enCamino = textosEnCamino.current;
    // Normalizado: `actualizarFicha` guarda el texto vacío como null (review #201).
    for (const k of TEXTOS_DEL_PEDIDO) if (k in enCamino && (enCamino[k] || null) === (producto[k] || null)) delete enCamino[k];
  }, [producto]);
  const textoActual = (k: TextoDelPedido) => (k in textosEnCamino.current ? textosEnCamino.current[k] : producto[k]);
  const ia = useMejorarConIa((campo) => ({
    nombre: textoActual('nombre') ?? producto.nombre,
    descripcion: textoActual('descripcion'),
    rubro,
    marca: textoActual('marca'),
    variantes: [...producto.variantes]
      .sort((a, b) => a.orden - b.orden)
      .map((v, j) => v.valores.filter(Boolean).join(' / ') || `Variante ${j + 1}`),
    categorias: [],
    campos: [campo],
    canal,
    // Las primeras: son la portada y las que mejor muestran el producto (como en el panel).
    fileNames: imagenes.slice(0, 4).map((im) => im.fileName),
    seoTitulo: textoActual('seoTitulo'),
    seoDescripcion: textoActual('seoDescripcion'),
  }));
  const propuestaDe = (campo: 'descripcion' | 'seo') => {
    const e = ia.de(campo);
    return e?.estado === 'listo' ? e.sugerencia : null;
  };
  const usarPropuesta = (campo: 'descripcion' | 'seo') => {
    const s = propuestaDe(campo);
    if (!s) return;
    ia.descartar(campo);
    void guardar(
      campo === 'descripcion'
        ? { descripcion: s.descripcion }
        : {
            ...(s.seoTitulo ? { seoTitulo: s.seoTitulo } : {}),
            ...(s.seoDescripcion ? { seoDescripcion: s.seoDescripcion } : {}),
          },
    );
  };

  /** El candado de una sección, o nada si este producto no traba (se crea, o es de antes). */
  /** Qué tienda se ve en Fotos (ADR-2026-10-30-un-cambio-en-varias-tiendas). */
  const [vistaDeFotos, setVistaDeFotos] = useState<'tiendanube' | 'mercadolibre'>('tiendanube');
  /** Los cinco de Tiendanube: el de la ficha, o el de la tienda si la ficha todavía no lo tiene. */
  const desdeLaTienda = publicado ? camposDesdeLaTienda(publicado) : null;
  const enLaFicha = {
    visibilidad: producto.visibilidad ?? desdeLaTienda?.visibilidad ?? null,
    envioGratis: producto.envioGratis ?? desdeLaTienda?.envioGratis ?? null,
    mpn: producto.mpn ?? desdeLaTienda?.mpn ?? null,
    rangoEdad: producto.rangoEdad ?? desdeLaTienda?.rangoEdad ?? null,
    genero: producto.genero ?? desdeLaTienda?.genero ?? null,
  };

  const candadoDe = (seccion: SeccionTrabable) => {
    if (seCrea || destrabadas === null || !publicado) return undefined;
    return {
      trabada: estaTrabada(destrabadas, seccion),
      enLaTienda: resumenEnLaTienda(seccion, publicado, categorias),
      sinMandar: (comparacion?.trabados ?? []).filter((c) => (c.candado ?? c.seccion) === seccion).length,
      onCambiar: (trabar: boolean) => cambiarTraba(seccion, trabar),
    };
  };
  const topeAtributos = reglas.maxAtributos ?? Infinity;
  const conVariantes = producto.atributos.length > 0;
  // Para el aviso de "Obligatorio" en Envío cuando el destino lo exige: cero o
  // vacío cuentan igual, VTEX rechaza los dos.
  const faltaPaquete = [producto.peso, producto.alto, producto.ancho, producto.profundidad].some(
    (m) => (aNumero(m) ?? 0) <= 0,
  );

  /**
   * Orden de las variantes aplicado en pantalla mientras el guardado viaja.
   *
   * Es solo el ORDEN, no una copia de las variantes: los campos (stock, SKU,
   * precio) tienen que seguir viniendo del prop, porque cada blur los guarda y
   * recarga. Una copia entera se quedaría con los valores viejos.
   *
   * Y se guarda SIN recargar, igual que el reordenar de fotos: un refetch acá
   * volvería a firmar todas las miniaturas y se verían parpadear todas las fotos
   * de todas las variantes por mover una de lugar
   * (ADR-2026-09-06-lanzamientos-reorden-optimista).
   */
  const [ordenLocal, setOrdenLocal] = useState<string[] | null>(null);

  const variantesDelServidor = useMemo(
    () => [...producto.variantes].sort((a, b) => a.orden - b.orden),
    [producto.variantes],
  );

  const variantesOrdenadas = useMemo(() => {
    if (!ordenLocal) return variantesDelServidor;
    const porId = new Map(variantesDelServidor.map((v) => [v.id, v]));
    const puestas = ordenLocal
      .map((id) => porId.get(id))
      .filter((v): v is VarianteDeLanzamiento => !!v);
    // Una variante creada después del último movimiento no está en el orden
    // local: va al final, que es donde la crea `agregarVariante`.
    const nuevas = variantesDelServidor.filter((v) => !ordenLocal.includes(v.id));
    return [...puestas, ...nuevas];
  }, [variantesDelServidor, ordenLocal]);

  // Cuando el servidor ya devuelve el orden que se aplicó en pantalla, el
  // optimista deja de hacer falta: soltarlo evita que se quede peleando con un
  // cambio hecho desde otro lado.
  //
  // Se comparan solo las variantes que están en los dos lados: una creada o
  // borrada después del movimiento cambia el largo de la lista pero no dice nada
  // sobre si el orden llegó, y sin esto el override no se soltaba nunca.
  useEffect(() => {
    if (!ordenLocal) return;
    const delServidor = variantesDelServidor.map((v) => v.id);
    const local = ordenLocal.filter((id) => delServidor.includes(id));
    const servidor = delServidor.filter((id) => local.includes(id));
    if (servidor.every((id, i) => id === local[i])) setOrdenLocal(null);
  }, [variantesDelServidor, ordenLocal]);

  /**
   * Abre una foto en grande. El visor aparece con el spinner y la URL completa
   * llega después: firmar antes de abrir dejaría medio segundo en el que tocar
   * la foto no hace nada.
   */
  const abrirFoto = (id: string) => {
    const foto = imagenes.find((i) => i.id === id);
    if (!foto || !companyId) return;
    setVerFoto({ id, url: null });
    void (async () => {
      const url = await firmarFull(companyId, foto.fileName);
      // Si mientras se firmaba se cerró el visor o se abrió otra foto, esta URL
      // ya no es la que hay que mostrar.
      setVerFoto((actual) => (actual?.id === id ? { id, url } : actual));
    })();
  };

  /**
   * Las cards viajan a su lugar nuevo en vez de aparecer ahí. Sin esto, cambiar
   * el orden con las flechas repinta la lista y listo: para el navegador no se
   * movió nada, se redibujó otra cosa en el mismo lugar, y no hay nada que
   * transicionar.
   */
  const refDeVariante = useReordenAnimado();

  const cambiarCategoria = (id: string, puesta: boolean) => {
    if (!companyId) return;
    const antes = suyas;
    // Con una sola categoría, tildar una DESTILDA las otras: el usuario elige
    // "va acá", y dejarle dos para que después la validación se queje sería
    // hacerle el trabajo dos veces.
    const reemplaza = puesta && reglas.unaSolaCategoria;
    setSuyas(reemplaza ? [id] : puesta ? [...antes, id] : antes.filter((x) => x !== id));
    void (async () => {
      const r = reemplaza
        ? await reemplazarCategoriaDeProducto(producto.id, id, companyId)
        : await marcarCategoriaDeProducto(producto.id, id, companyId, puesta);
      if (!r.error) return;
      setErrorFoto(r.error);
      // Un reemplazo son DOS escrituras y no hay transacción desde el navegador: si falla
      // la segunda, la base quedó a mitad de camino. Volver a pintar lo de antes —lo que
      // se hacía— mostraba una cosa mientras la base tenía otra, y al recargar cambiaba
      // sola la validación de "listo para publicar". Se relee el lanzamiento y se pinta lo
      // que hay de verdad; si ni eso se puede, recién ahí se vuelve a lo de antes.
      try {
        await onCambio();
      } catch {
        setSuyas(antes);
      }
    })();
  };

  /** Mueve una variante un escalón. Se aplica al instante y se guarda atrás. */
  const moverVariante = (id: string, hacia: -1 | 1) => {
    const ids = variantesOrdenadas.map((v) => v.id);
    const siguiente = moverUnEscalon(ids, id, hacia);
    if (siguiente.every((x, i) => x === ids[i])) return;
    setOrdenLocal(siguiente);
    setErrorFoto(null);
    void (async () => {
      const r = await reordenarVariantes(siguiente);
      if (r.error) {
        setOrdenLocal(ids);
        setErrorFoto(r.error);
      }
    })();
  };

  /**
   * Si las fotos se muestran por variante o en el producto.
   *
   * Con la opción prendida pero sin ninguna variante todavía, las fotos siguen
   * siendo del producto: esconderlas no tendría dónde mostrarlas. Pasan a la
   * primera variante en cuanto exista. Y solo aplica al crear: un producto que
   * ya está en la tienda no muestra la sección de variantes.
   */
  const fotosPorVariante =
    seCrea &&
    separaImagenesPorVariante(producto.imagenesPorVariante, reglas) &&
    variantesOrdenadas.length > 0;

  /**
   * Aplica un orden global nuevo: en pantalla al instante, en la base atrás.
   * Es el mismo camino para la tira del producto y para la de cada variante —
   * lo que se guarda siempre es el orden del PRODUCTO, porque es el que define
   * la portada y la galería.
   */
  const aplicarOrden = (idsEnOrden: string[]) => {
    const antes = imagenes;
    setErrorFoto(null);
    setImagenes(
      idsEnOrden
        .map((id) => antes.find((i) => i.id === id))
        .filter((i): i is (typeof antes)[number] => !!i),
    );
    void (async () => {
      const r = await onReordenarImagenes(idsEnOrden);
      if (r?.error) {
        setImagenes(antes);
        setErrorFoto(r.error);
      }
    })();
  };

  const quitarFoto = (id: string) => {
    const antes = imagenes;
    setErrorFoto(null);
    setImagenes(antes.filter((i) => i.id !== id));
    void (async () => {
      const r = await onQuitarImagen(id);
      if (r?.error) {
        setImagenes(antes);
        setErrorFoto(r.error);
      }
    })();
  };

  /** Mover una foto a otra variante. Optimista como el reordenar: recargar acá
   *  volvería a firmar todas las miniaturas y se vería el parpadeo. */
  const moverFoto = (imagenId: string, varianteId: string) => {
    const antes = imagenes;
    setErrorFoto(null);
    setImagenes(
      antes.map((i) => (i.id === imagenId ? { ...i, lanzamientoVarianteId: varianteId } : i)),
    );
    void (async () => {
      const r = await asignarImagenAVariante(imagenId, varianteId);
      if (r.error) {
        setImagenes(antes);
        setErrorFoto(r.error);
      }
    })();
  };

  /** Las propiedades (Talle: S, M, L…), leídas de las variantes. */
  const propiedades = useMemo(() => propiedadesDe(producto), [producto]);
  /** Qué propiedad se está editando en el panel; `indice: null` = una nueva. */
  const [editor, setEditor] = useState<{ indice: number | null; nombre?: string } | null>(null);
  /** Lo que salió mal al aplicar una propiedad (quitar un chip o la propiedad entera). */
  const [errorPropiedades, setErrorPropiedades] = useState<string | null>(null);

  /** Atributos que se ofrecen para este rubro, sin los que ya están puestos. */
  const sugeridos = useMemo(
    () => atributosSugeridos(rubro, producto.atributos),
    [rubro, producto.atributos],
  );

  const hayQueConfigurar = hayConfiguracionDeVariantes(reglas);

  /** Qué dimensiones están separadas, para el resumen del botón. */
  const separadas = useMemo(
    () =>
      [
        reglas.conPrecioYStock && producto.precioPorVariante ? 'precio' : null,
        reglas.conEnvio && producto.envioPorVariante ? 'envío' : null,
        separaImagenesPorVariante(producto.imagenesPorVariante, reglas) ? 'imágenes' : null,
      ].filter((x): x is string => x !== null),
    [producto.precioPorVariante, producto.envioPorVariante, producto.imagenesPorVariante, reglas],
  );

  // Con el árbol, para que "una sola categoría" pueda contarlas.
  const problemas = useMemo(
    () => (seCrea ? problemasDeFicha(fichaDe(producto, categorias), reglas) : []),
    [producto, seCrea, reglas, categorias],
  );

  const guardar = async (campos: CamposDeFicha) => {
    for (const k of TEXTOS_DEL_PEDIDO) if (k in campos) textosEnCamino.current[k] = campos[k];
    setGuardando(true);
    await actualizarFicha(producto.id, campos);
    await onCambio();
    setGuardando(false);
  };

  /** La marca de "cambia en la tienda" de un campo y cómo deshacerlo. */
  const marcaDe = (campo: string): { cambio: CambioDeCampo | null; onDeshacer?: () => void } => {
    const c = comparacion?.cambios.find((x) => x.campo === campo) ?? null;
    if (!c || c.restaurar === undefined) return { cambio: c };
    const valor = c.restaurar;
    return {
      cambio: c,
      onDeshacer: () =>
        void guardar(
          campo === 'stock'
            ? { stock: valor === '' ? null : (Number(valor) as never) }
            : ({ [campo]: valor } as CamposDeFicha),
        ),
    };
  };
  const cambiosDe = (seccion: CambioDeCampo['seccion']) => (comparacion?.cambios ?? []).filter((c) => c.seccion === seccion);

  const guardarVariante = async (id: string, campos: Partial<VarianteDeLanzamiento>) => {
    setGuardando(true);
    await actualizarVariante(id, campos);
    await onCambio();
    setGuardando(false);
  };

  /**
   * Prender o apagar "imágenes por variante".
   *
   * Al PRENDER, las fotos que todavía son del producto pasan a la primera
   * variante: la sección del producto se esconde y, si no fueran a ningún lado,
   * el usuario las vería desaparecer. Al apagar no se toca nada — la asignación
   * deja de usarse al publicar, y si vuelve a prender encuentra su reparto tal
   * como lo dejó en vez de todo amontonado otra vez en la primera.
   */
  const cambiarFotosPorVariante = async (activo: boolean) => {
    setGuardando(true);
    await actualizarFicha(producto.id, { imagenesPorVariante: activo });
    const primera = variantesOrdenadas[0];
    if (activo && primera) await asignarSueltasAVariante(producto.id, primera.id);
    await onCambio();
    setGuardando(false);
  };

  /**
   * Deja las variantes como la combinación de estas propiedades
   * (ADR-2026-10-06-variantes-por-propiedades). Las que siguen conservan lo que
   * tenían; sólo se crean las combinaciones nuevas y se borran las que ya no
   * existen. Si al borrar se pierde algo cargado (stock, precio, fotos), pregunta.
   *
   * Devuelve el error, o `null`. El orden de las escrituras importa (review #168): primero las
   * variantes y AL FINAL los nombres de las propiedades. Si algo falla en el medio, se frena, se
   * vuelve a leer lo que quedó guardado y se avisa. Con los nombres de antes todavía puestos, volver
   * a aplicar lleva al mismo resultado en vez de dejar una propiedad sin valores.
   */
  const aplicarSinCandado = async (nuevas: Propiedad[], preguntar: boolean): Promise<string | null> => {
    if (!companyId) return 'No se encontró tu empresa. Recargá la página.';
    const plan = planDeVariantes(producto, nuevas);
    if (preguntar && plan.quitar.length > 0) {
      const conDatos = plan.quitar.filter((id) => {
        const v = producto.variantes.find((x) => x.id === id);
        return !!v && (!!v.stock || !!v.sku || !!v.precio || fotosDeVariante(imagenes, id).length > 0);
      });
      if (
        conDatos.length > 0 &&
        !window.confirm(
          `Se van a borrar ${plan.quitar.length} variante${plan.quitar.length === 1 ? '' : 's'}, con su stock, precio y fotos. ¿Seguimos?`,
        )
      )
        return null;
    }
    setGuardando(true);
    setErrorPropiedades(null);
    const frenar = async (mensaje: string) => {
      await onCambio();
      setGuardando(false);
      setErrorPropiedades(mensaje);
      return mensaje;
    };
    const quitadas = await Promise.all(plan.quitar.map((id) => quitarVariante(id)));
    if (quitadas.some((r) => r.error)) return frenar('No se pudieron quitar todas las variantes. Probá de nuevo.');
    const actualizadas = await Promise.all(plan.actualizar.map((u) => actualizarVariante(u.id, { valores: u.valores })));
    if (actualizadas.some((r) => r.error)) return frenar('No se pudieron actualizar todas las variantes. Probá de nuevo.');
    const creadas = await Promise.all(
      plan.crear.map((c) => agregarVariante(producto.id, companyId, c.valores, c.orden)),
    );
    if (creadas.some((id) => !id)) return frenar('No se pudieron crear todas las variantes. Probá de nuevo.');
    const ids = plan.orden
      .map((o) => ('id' in o ? o.id : creadas[o.nueva]))
      .filter((id): id is string => !!id);
    if (ids.length > 0) {
      const r = await reordenarVariantes(ids);
      if (r.error) return frenar(r.error);
    }
    const ficha = await actualizarFicha(producto.id, { atributos: plan.atributos });
    if (ficha.error) return frenar('No se pudieron guardar las propiedades. Probá de nuevo.');
    // Con fotos por variante, las que quedaron sueltas (del producto o de una variante
    // borrada) van a la primera: si no, no se verían en ninguna tira.
    if (producto.imagenesPorVariante && ids[0]) await asignarSueltasAVariante(producto.id, ids[0]);
    await onCambio();
    setGuardando(false);
    return null;
  };

  /**
   * Un guardado de propiedades a la vez (review #168). Dos superpuestos —cerrar el panel mientras
   * guarda y aplicar otra vez, o tocar la × de un chip— planificaban sobre las MISMAS variantes viejas
   * y creaban dos veces las combinaciones que faltaban: variantes duplicadas que llegaban a publicarse.
   */
  const aplicandoPropiedades = useRef(false);
  const aplicarPropiedades = async (nuevas: Propiedad[], preguntar = false): Promise<string | null> => {
    if (aplicandoPropiedades.current) return 'Ya se está guardando un cambio. Esperá a que termine.';
    aplicandoPropiedades.current = true;
    try {
      return await aplicarSinCandado(nuevas, preguntar);
    } finally {
      aplicandoPropiedades.current = false;
    }
  };

  return (
    <div>
      {verFoto && <VisorDeFoto url={verFoto.url} onCerrar={() => setVerFoto(null)} />}

      {/* Dos columnas en escritorio, como la ficha de producto de Shopify o Wix: a la
          izquierda lo que se publica (fotos, datos, precio, variantes) y a la derecha
          el estado y cómo se organiza (categorías, SEO). En el teléfono, una sola
          columna con el estado arriba (ADR-2026-10-06-la-ficha-ocupa-la-pantalla).
          `grid-rows-[auto_1fr]`: la columna izquierda ocupa las dos filas, y sin esto
          el alto que le sobra se repartiría entre las dos y quedaría un hueco entre el
          estado y las categorías. */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] lg:grid-rows-[auto_1fr] lg:items-start">
        <div className="space-y-3 lg:col-start-2 lg:row-start-1">
          <TarjetaDeEstado
            producto={producto}
            fotos={imagenes.length}
            seCrea={seCrea}
            zip={reglas.salida === 'zip'}
            destinos={tiendas}
            problemas={problemas}
            guardando={guardando}
            comentariosPendientes={comentariosPendientes}
            fotosEnVariantes={fotosPorVariante}
            onCompletarConIa={onCompletarConIa}
            onAbrirComentarios={onAbrirComentarios}
          />
          {lecturaDeLaTienda && (
            <EnTuTienda
              lectura={lecturaDeLaTienda}
              comparacion={comparacion}
              trabadas={
                candadoDe('basico') === undefined
                  ? null
                  : SECCIONES_TRABABLES.filter(
                      (s) =>
                        estaTrabada(destrabadas, s) &&
                        (s === 'precio' || s === 'inventario'
                          ? reglas.conPrecioYStock
                          : s === 'envio'
                            ? reglas.conEnvio
                            : s === 'seo'
                              ? reglas.conMarcaYSeo
                              : true),
                    ).map((s) => ({ id: s, titulo: TITULO_DE_SECCION[s] }))
              }
              traerTraba={!seCrea}
              onReleer={() => onReleerTienda?.()}
              onTraer={async () => {
                if (lecturaDeLaTienda.estado !== 'listo' || !companyId) return 'No se pudo leer tu tienda.';
                setGuardando(true);
                // Lo de la tienda pisa lo que estaba por guardarse: el pedido de la IA lee la ficha.
                textosEnCamino.current = {};
                // Traer deja la ficha como la tienda y vuelve a trabar todo
                // (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas).
                const r = await traerLoDeLaTienda(producto, lecturaDeLaTienda.publicado, categorias, companyId, {
                  trabar: !seCrea,
                });
                await onCambio();
                setGuardando(false);
                return r.error;
              }}
            />
          )}
        </div>

        <div className="min-w-0 space-y-3 lg:col-start-1 lg:row-span-2 lg:row-start-1">
        {/* Qué hacer con las fotos que el producto YA tiene en la tienda. Es un
            interruptor y no dos botones: es una cosa o la otra, y como botones
            sueltos no se leía cuál estaba elegida. Va ARRIBA de las fotos porque
            decide qué pasa con ellas; abajo quedaba después de haberlas armado.
            Las dos opciones se ven igual al elegirlas: lo que borra "Reemplazar"
            lo avisa la línea roja de abajo, no el color del control. */}
        {!seCrea && !fotosSueltas && (
          <div data-tour="ficha-accion">
            <div
              role="radiogroup"
              aria-label="Qué hacer con las fotos que ya tiene en la tienda"
              // Pista y pastilla con el MISMO radio (redondas las dos): con radios
              // distintos la pastilla elegida no acompañaba el borde de la pista.
              // Gris neutro fijo y no `neutral-100`, que en este proyecto es un
              // gris azulado (tokens pisados en `tailwind.config.js`).
              className="flex gap-1 rounded-full bg-[#F2F2F2] p-1"
            >
              {(['add', 'replace'] as const).map((a) => {
                const activa = producto.accion === a;
                return (
                  <button
                    key={a}
                    type="button"
                    role="radio"
                    aria-checked={activa}
                    aria-describedby={`ayuda-accion-${a}`}
                    onClick={() => !activa && void guardar({ accion: a })}
                    className={`group/opcion flex flex-1 items-center justify-center gap-1.5 rounded-full py-2 px-3 text-xs font-medium transition-colors ${
                      activa ? 'bg-white text-brand-black shadow-sm' : 'text-black/35 hover:text-black/60'
                    }`}
                  >
                    {a === 'add' ? (
                      <ImagePlus className="w-3.5 h-3.5" />
                    ) : (
                      <ArrowLeftRight className="w-3.5 h-3.5" />
                    )}
                    {a === 'add' ? 'Agregar al carrusel' : 'Reemplazar las fotos'}
                    {/* El "?" vive ADENTRO de la opción: es de ella, no del grupo. Un
                        toque en él abre la ayuda y NO elige la opción — si eligiera,
                        preguntar qué hace "Reemplazar" ya lo haría. Con teclado, la
                        ayuda sale al llegar a la opción con Tab (`focus-visible`): un
                        botón propio para el "?" no puede ir adentro de otro botón
                        (review Greptile #155), y `aria-describedby` ya se la lee a
                        un lector de pantalla. */}
                    <span
                      className="group/ayuda relative -my-1 ml-0.5 grid h-5 w-5 place-items-center rounded-full text-black/30 hover:text-black/60"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAyudaDeAccion((v) => (v === a ? null : a));
                      }}
                      onMouseLeave={() => setAyudaDeAccion(null)}
                    >
                      <HelpCircle className="w-3.5 h-3.5" />
                      <span
                        id={`ayuda-accion-${a}`}
                        role="tooltip"
                        className={`pointer-events-none absolute bottom-full left-1/2 z-30 mb-2 w-60 -translate-x-1/2 rounded-xl bg-brand-black py-2 px-3 text-left text-[12px] font-normal leading-[17px] text-white shadow-lg transition-opacity ${
                          ayudaDeAccion === a
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/ayuda:opacity-100 group-focus-visible/opcion:opacity-100'
                        }`}
                      >
                        {a === 'add'
                          ? 'Las fotos de acá se suman al final de las que el producto ya tiene en tu tienda. No se borra ninguna.'
                          : 'Se borran las fotos que el producto tiene hoy en tu tienda y quedan solo las de acá, en este orden.'}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {producto.accion === 'replace' && (
              // El mismo cartel rojo que "lo que falta" de la ficha: es lo único que
              // dice que esta opción borra algo, así que se tiene que leer.
              <p
                role="alert"
                className="mt-2 flex items-start gap-2 rounded-xl bg-red-50 py-2.5 px-3 text-[13px] text-red-700"
              >
                <AlertCircle className="mt-0.5 w-4 h-4 shrink-0" />
                Se borran las fotos que el producto tenga hoy en la tienda.
              </p>
            )}
          </div>
        )}

        {/* Las fotos primero y sin plegar: son lo que se está publicando y el
            orden define la portada. Es la card dominante de la ficha; el resto
            son formularios y quedan más quietos.

            Con las fotos por variante esta sección DESAPARECE: cada foto vive en
            la card de su variante. Dejarla acá además sería mostrar las mismas
            fotos dos veces. */}
        {!fotosPorVariante && (
          <div data-tour="ficha-fotos">
          <Seccion
            id="fotos"
            titulo="Fotos"
            icono={<Images className="w-4 h-4" />}
            plegable={false}
            resumen={`${imagenes.length} de ${MAX_IMAGENES_POR_PRODUCTO}`}
          >
            {mercadoLibre && <SelectorDeTiendaDeFotos vista={vistaDeFotos} onCambiar={setVistaDeFotos} />}
            {mercadoLibre && vistaDeFotos === 'mercadolibre' ? (
              <FotosEnMl {...mercadoLibre} fotosNuevas={imagenes.length} />
            ) : (
            <>
            {lecturaDeLaTienda?.estado === 'listo' && lecturaDeLaTienda.publicado.imagenes.length > 0 && (
              <FotosEnLaTienda
                imagenes={lecturaDeLaTienda.publicado.imagenes}
                seBorran={!fotosSueltas && producto.accion === 'replace'}
                aBorrar={fotosSueltas ? (producto.fotosABorrar ?? []) : null}
                onCambiarABorrar={async (ids) => {
                  await guardarFotosABorrar(producto.id, ids);
                  await onCambio();
                }}
              />
            )}
            <TiraDeFotos
              imagenes={imagenes}
              sinLugar={sinLugar}
              subiendo={subiendoEn === PRODUCTO}
              onReordenar={aplicarOrden}
              onQuitar={quitarFoto}
              onElegirGenerada={() => setDestinoDeAlta(PRODUCTO)}
              onSubir={(archivo) => void subirFoto(archivo, null)}
              onVer={abrirFoto}
            />
            {errorFoto && <p className="text-xs text-red-600">{errorFoto}</p>}
            <p className="text-[11px] text-neutral-400">
              {imagenes.length === 0
                ? lecturaDeLaTienda?.estado === 'listo' && lecturaDeLaTienda.publicado.imagenes.length > 0
                  ? 'Sumá fotos nuevas acá, o mandale una desde “Publicar en tu tienda online” en Creaciones.'
                  : 'Sumá la primera foto, o mandale una desde “Publicar en tu tienda online” en Creaciones.'
                : producto.accion === 'add' || fotosSueltas
                  ? 'Se agregan al final de las que ya tiene en la tienda. Arrastrá para reordenarlas.'
                  : 'La primera es la portada. Arrastrá para reordenarlas.'}
            </p>
            {producto.imagenesPorVariante && (
              <p className="text-[11px] text-neutral-400">
                Estas fotos pasan a la primera variante en cuanto cargues una.
              </p>
            )}
            </>
            )}
          </Seccion>
          </div>
        )}

        <SelectorDeCreaciones
          abierta={destinoDeAlta !== null}
          companyId={companyId}
          varianteIds={varianteIdsDelProducto}
          yaUsadas={generadasYaUsadas}
          onCerrar={() => setDestinoDeAlta(null)}
          onConfirmar={async (elegidas) => {
            if (!companyId) return;
            setErrorFoto(null);
            const r = await sumarGeneradasAProducto(
              producto.id,
              companyId,
              elegidas.map((e) => ({ id: e.id, varianteId: e.varianteId })),
              destinoDeAlta === PRODUCTO ? null : destinoDeAlta,
            );
            if (r.error) setErrorFoto(r.error);
            setDestinoDeAlta(null);
            await onCambio();
          }}
        />


        {/* La ficha completa: al crear, todo; si el producto ya está en la tienda, se edita contra lo
            publicado y cada campo que cambia se marca. */}
        {muestraLaFicha && (
          <>
            <Seccion
              id="basico"
              candado={candadoDe('basico')}
              titulo="Lo básico"
              icono={<Tag className="w-4 h-4" />}
              plegable={false}
              aviso={!producto.nombre?.trim() ? 'Falta el nombre' : null}
            >
              <Campo
                etiqueta="Nombre"
                valor={producto.nombre ?? ''}
                requerido
                maxLength={reglas.maxNombre ?? undefined}
                {...marcaDe('nombre')} onGuardar={(v) => void guardar({ nombre: v })}
              />
              {reglas.conMarcaYSeo && (
                <Campo
                  etiqueta="Descripción"
                  multilinea
                  valor={producto.descripcion ?? ''}
                  placeholder="Opcional"
                  {...marcaDe('descripcion')} onGuardar={(v) => void guardar({ descripcion: v })}
                  accion={
                    mejora && (
                      <BotonMejorarConIa
                        campo="descripcion"
                        pensando={ia.de('descripcion')?.estado === 'pensando'}
                        onPedir={() => ia.pedir('descripcion')}
                      />
                    )
                  }
                />
              )}
              {mejora && (
                <PropuestaDeIa
                  estado={ia.de('descripcion')}
                  lineas={[{ texto: propuestaDe('descripcion')?.descripcion ?? '' }]}
                  onUsar={() => usarPropuesta('descripcion')}
                  onOtra={() => ia.pedir('descripcion')}
                  onDescartar={() => ia.descartar('descripcion')}
                />
              )}
            </Seccion>

            {reglas.conPrecioYStock && (
              <Seccion
                id="precio"
                candado={candadoDe('precio')}
                titulo="Precio y costo"
                icono={<Wallet className="w-4 h-4" />}
                aviso={
                  !conVariantes && !producto.precio?.trim() ? 'Falta el precio' : null
                }
                abiertaPorDefecto
                resumen={producto.precio ? `$${producto.precio}` : 'Sin precio'}
              >
                <div className="grid gap-3 sm:grid-cols-3">
                  <Campo
                    etiqueta="Precio"
                    tipo="number"
                    requerido
                    valor={producto.precio ?? ''}
                    placeholder="0"
                    {...marcaDe('precio')} onGuardar={(v) => void guardar({ precio: v })}
                  />
                  <Campo
                    etiqueta="Costo"
                    tipo="number"
                    valor={producto.costo ?? ''}
                    placeholder="Opcional"
                    ayuda="No se publica: es tuyo"
                    {...marcaDe('costo')} onGuardar={(v) => void guardar({ costo: v })}
                  />
                  <Margen precio={producto.precio} costo={producto.costo} />
                </div>
                {conVariantes && producto.precioPorVariante && (
                  <p className="text-[11px] text-neutral-400">
                    Las variantes que no definan el suyo usan estos valores.
                  </p>
                )}
              </Seccion>
            )}

            {reglas.conPrecioYStock && (
              <Seccion
                id="inventario"
                candado={candadoDe('inventario')}
                titulo="Inventario"
                icono={<Package className="w-4 h-4" />}
                abiertaPorDefecto={Boolean(producto.sku || producto.codigoBarras || producto.stock)}
                resumen={[producto.sku, producto.stock != null ? `${producto.stock} en stock` : null]
                  .filter(Boolean)
                  .join(' · ')}
              >
                <div className="grid gap-3 sm:grid-cols-3">
                  <Campo
                    etiqueta="Stock"
                    tipo="number"
                    valor={producto.stock != null ? String(producto.stock) : ''}
                    placeholder="Sin definir"
                    {...marcaDe('stock')}
                    onGuardar={(v) => void guardar({ stock: v === '' ? null : (Number(v) as never) })}
                  />
                  <Campo
                    etiqueta="SKU"
                    valor={producto.sku ?? ''}
                    placeholder="Opcional"
                    {...marcaDe('sku')} onGuardar={(v) => void guardar({ sku: v })}
                  />
                  <Campo
                    etiqueta="Código de barras"
                    valor={producto.codigoBarras ?? ''}
                    placeholder="EAN, UPC, ISBN…"
                    {...marcaDe('codigoBarras')} onGuardar={(v) => void guardar({ codigoBarras: v })}
                  />
                </div>
              </Seccion>
            )}

            {reglas.conEnvio && (
            <Seccion
              id="envio"
              candado={candadoDe('envio')}
              titulo="Envío"
              icono={<Truck className="w-4 h-4" />}
              abiertaPorDefecto={
                Boolean(producto.peso || producto.alto || producto.ancho || producto.profundidad) ||
                reglas.envioObligatorio
              }
              aviso={reglas.envioObligatorio && faltaPaquete ? 'Obligatorio' : null}
              resumen={
                producto.peso || producto.alto
                  ? `${producto.peso ? `${producto.peso} kg` : ''}${producto.peso && producto.alto ? ' · ' : ''}${
                      producto.alto ? `${producto.alto}×${producto.ancho ?? '?'}×${producto.profundidad ?? '?'} cm` : ''
                    }`
                  : 'Sin peso ni medidas'
              }
            >
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Campo etiqueta="Peso" tipo="number" sufijo="kg" valor={producto.peso ?? ''} {...marcaDe('peso')} onGuardar={(v) => void guardar({ peso: v })} />
                <Campo etiqueta="Alto" tipo="number" sufijo="cm" valor={producto.alto ?? ''} {...marcaDe('alto')} onGuardar={(v) => void guardar({ alto: v })} />
                <Campo etiqueta="Ancho" tipo="number" sufijo="cm" valor={producto.ancho ?? ''} {...marcaDe('ancho')} onGuardar={(v) => void guardar({ ancho: v })} />
                <Campo etiqueta="Profundidad" tipo="number" sufijo="cm" valor={producto.profundidad ?? ''} {...marcaDe('profundidad')} onGuardar={(v) => void guardar({ profundidad: v })} />
              </div>
              <p className="text-[11px] text-neutral-400">{ayudaDeEnvio(reglas)}</p>
            </Seccion>
            )}

            <Seccion
              id="variantes"
              candado={candadoDe('variantes')}
              titulo="Variantes"
              icono={<Layers className="w-4 h-4" />}
              abiertaPorDefecto={conVariantes}
              resumen={
                conVariantes
                  ? `${producto.atributos.filter(Boolean).join(' · ')} — ${producto.variantes.length} variante${producto.variantes.length === 1 ? '' : 's'}`
                  : 'Producto sin variantes'
              }
            >
              <ListaDeCambios cambios={cambiosDe('variantes')} />
              <p className="text-[11px] text-neutral-500">
                Elegí qué cambia de una variante a otra (talle, color…) y sus valores: las
                variantes se arman solas, una por combinación.{' '}
                {/* Qué es una variante depende del destino: en una tienda es una
                    fila con su stock y su precio; en el ZIP es una carpeta.
                    Nombrar campos que este destino no tiene manda al usuario a
                    buscarlos. */}
                {reglas.salida === 'zip'
                  ? 'Cada una tiene su carpeta en el ZIP, con sus fotos numeradas desde 1.'
                  : 'Cada una lleva su stock y su SKU; el precio, el envío y las fotos salen del producto salvo que los separes.'}
                {reglas.skuOCodigoPorVariante &&
                  ' En VTEX cada variante necesita su propio SKU o código de barras, sin repetir.'}
              </p>

              {/* Qué se separa por variante vive detrás de un botón: se toca una
                  vez al armar el producto, no en cada edición. Lo que no se
                  prenda sigue saliendo del producto.
                  Es UNA card: el botón es su encabezado y los interruptores son
                  filas separadas por una línea, no tres cards sueltas. */}
              {conVariantes && hayQueConfigurar && (
              <div className="overflow-hidden rounded-xl border border-neutral-200">
                <button
                  type="button"
                  onClick={() => setConfigAbierta((v) => !v)}
                  aria-expanded={configAbierta}
                  className="flex w-full items-center justify-between gap-3 py-3 px-4 text-left transition-colors hover:bg-neutral-50"
                >
                  <span className="flex items-center gap-2">
                    <Settings2 className="w-4 h-4 text-neutral-400" />
                    <span className="text-sm text-brand-black">Configuración de variantes</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {separadas.length > 0 && (
                      <span className="hidden text-[11px] text-neutral-400 sm:inline">
                        {separadas.join(' · ')}
                      </span>
                    )}
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform ${configAbierta ? 'rotate-180' : ''}`}
                    />
                  </span>
                </button>
                {configAbierta && (
                  <div>
                    {reglas.conPrecioYStock && (
                      <InterruptorPorVariante
                        titulo="Precio por variante"
                        detalle="Precio, costo y código de barras propios de cada una. El stock y el SKU ya lo son siempre."
                        activo={producto.precioPorVariante}
                        onCambiar={(v) => void guardar({ precioPorVariante: v })}
                      />
                    )}
                    {reglas.conEnvio && (
                      <InterruptorPorVariante
                        titulo="Datos de envío por variante"
                        detalle="Peso y medidas propios. Sirve cuando un talle pesa distinto."
                        activo={producto.envioPorVariante}
                        onCambiar={(v) => void guardar({ envioPorVariante: v })}
                      />
                    )}
                    {/* En el ZIP no hay interruptor: las carpetas SON la separación por
                        variante, y apagarlo amontonaba todos los colores en una sola
                        —justo el trabajo que el usuario venía a evitar
                        (ADR-2026-09-14-en-el-zip-las-fotos-van-siempre-por-variante). */}
                    {!reglas.imagenesPorVarianteFija && (
                      <InterruptorPorVariante
                        titulo="Imágenes diferentes por variante"
                        detalle="Cada variante maneja sus propias fotos. Tiendanube muestra la primera de cada una."
                        activo={producto.imagenesPorVariante}
                        onCambiar={(v) => void cambiarFotosPorVariante(v)}
                      />
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Las propiedades con sus valores como chips, como en Tienda Nube. El nombre
                  abre el panel para editarla; la × de un chip quita ese valor (y sus
                  variantes) sin abrir nada. */}
              {propiedades.length === 0 ? (
                <div className="rounded-xl border border-dashed border-neutral-300 p-4">
                  <p className="text-sm font-medium text-brand-black">¿Viene en distintos talles, colores…?</p>
                  <p className="mt-0.5 text-xs text-neutral-500">Elegí qué cambia y armamos las variantes por vos.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {sugeridos.map((nombre) => (
                      <button
                        key={nombre}
                        type="button"
                        disabled={!companyId}
                        onClick={() => setEditor({ indice: null, nombre })}
                        className="flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white py-1.5 px-3.5 text-sm text-brand-black transition-colors hover:border-brand-primary hover:bg-[rgb(var(--brand-primary-rgb)_/_0.045)]"
                      >
                        <Plus className="h-3.5 w-3.5 text-brand-primary" />
                        {nombre}
                      </button>
                    ))}
                    <button
                      type="button"
                      disabled={!companyId}
                      onClick={() => setEditor({ indice: null })}
                      className="rounded-full py-1.5 px-3 text-sm text-neutral-500 hover:bg-neutral-100 hover:text-brand-black"
                    >
                      Otra
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {propiedades.map((p, i) => {
                    const deColor = esAtributoColor(p.nombre) && reglas.salida !== 'zip';
                    return (
                      <div key={p.nombre + i} className="rounded-xl border border-neutral-200 py-3 px-3.5">
                        <div className="flex items-center justify-between gap-2">
                          <button
                            type="button"
                            onClick={() => setEditor({ indice: i })}
                            disabled={guardando}
                            className="flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:underline disabled:opacity-50"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {p.nombre || `Propiedad ${i + 1}`}
                          </button>
                          <button
                            type="button"
                            aria-label={`Quitar la propiedad ${p.nombre}`}
                            title="Quitar la propiedad"
                            onClick={() => void aplicarPropiedades(propiedades.filter((_, j) => j !== i), true)}
                            disabled={guardando}
                            className="disabled:opacity-40 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {p.valores.map((v, k) => (
                            <span
                              key={v.texto}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white py-1 pl-2.5 pr-1 text-sm text-brand-black"
                            >
                              {deColor && (
                                <span
                                  className="h-3 w-3 rounded-full border border-black/10"
                                  style={{ backgroundColor: colorDeTiendanube(v.texto)?.hex ?? 'transparent' }}
                                  aria-hidden="true"
                                />
                              )}
                              {v.texto}
                              <button
                                type="button"
                                aria-label={`Quitar ${v.texto}`}
                                disabled={guardando}
                                onClick={() =>
                                  void aplicarPropiedades(
                                    propiedades.map((o, j) => (j === i ? { ...o, valores: o.valores.filter((_, m) => m !== k) } : o)),
                                    true,
                                  )
                                }
                                className="grid h-5 w-5 place-items-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                          <button
                            type="button"
                            onClick={() => setEditor({ indice: i })}
                            className="inline-flex items-center gap-1 rounded-lg border border-dashed border-neutral-300 py-1 px-2.5 text-sm text-neutral-500 hover:border-neutral-400 hover:text-brand-black"
                          >
                            <Plus className="h-3.5 w-3.5" /> Agregar
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {propiedades.length < topeAtributos && (
                    <button
                      type="button"
                      onClick={() => setEditor({ indice: null })}
                      className="flex items-center gap-1.5 py-1 text-sm font-medium text-brand-primary hover:underline"
                    >
                      <Plus className="h-4 w-4" /> Agregar otra propiedad
                    </button>
                  )}
                </div>
              )}

              {errorPropiedades && !editor && (
                <p role="alert" className="mt-2 rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">
                  {errorPropiedades}
                </p>
              )}

              {editor && (
                <EditorDePropiedad
                  propiedades={propiedades}
                  indice={editor.indice}
                  nombreInicial={editor.nombre}
                  sugeridos={sugeridos}
                  variantesAntes={producto.variantes.length}
                  maxVariantes={reglas.maxVariantes}
                  colorComoTexto={reglas.salida === 'zip'}
                  onAplicar={(nuevas) => aplicarPropiedades(nuevas)}
                  onCerrar={() => {
                    setEditor(null);
                    setErrorPropiedades(null);
                  }}
                />
              )}

              {conVariantes && (
                <div className="mt-3 space-y-2">
                  {reglas.conPrecioYStock && (
                    // Mismas medidas que la fila (borde incluido) para que cada título quede sobre su columna.
                    <div className="hidden items-center gap-x-3 px-[13px] text-[11px] font-medium text-neutral-500 sm:flex">
                      <span className="flex-1">
                        {producto.variantes.length} variante{producto.variantes.length === 1 ? '' : 's'}
                      </span>
                      <span className="grid grid-cols-[112px_160px] gap-2">
                        <span>Stock</span>
                        <span>SKU</span>
                      </span>
                      {/* Lo que ocupan las acciones de cada fila. */}
                      <span className="w-[88px]" aria-hidden="true" />
                    </div>
                  )}
                  {variantesOrdenadas.map((v, i) => {
                    const fotos = fotosDeVariante(imagenes, v.id);
                    return (
                      <FilaDeVariante
                        key={v.id}
                        nodoRef={refDeVariante(v.id)}
                        variante={v}
                        puedeSubir={i > 0}
                        puedeBajar={i < variantesOrdenadas.length - 1}
                        onMoverArriba={() => moverVariante(v.id, -1)}
                        onMoverAbajo={() => moverVariante(v.id, 1)}
                        etiqueta={etiquetaDeVariante(v, i)}
                        colorHex={(() => {
                          const k = producto.atributos.findIndex((a) => esAtributoColor(a));
                          return k >= 0 && reglas.salida !== 'zip' ? colorDeTiendanube(v.valores[k])?.hex ?? null : null;
                        })()}
                        reglas={reglas}
                        // Doble llave: el interruptor del producto y que el
                        // destino tenga esos campos. Sin lo segundo, un
                        // lanzamiento que cambiara de destino mostraría campos
                        // que ya no existen.
                        porPrecio={reglas.conPrecioYStock && producto.precioPorVariante}
                        porEnvio={reglas.conEnvio && producto.envioPorVariante}
                        porImagenes={fotosPorVariante}
                        fotos={fotos}
                        sinLugar={sinLugar}
                        subiendo={subiendoEn === v.id}
                        destinos={variantesOrdenadas
                          .filter((o) => o.id !== v.id)
                          .map((o, j) => ({ id: o.id, etiqueta: etiquetaDeVariante(o, j) }))}
                        onGuardar={(campos) => void guardarVariante(v.id, campos)}
                        // Se arrastra dentro de UNA tira pero lo que se guarda es
                        // el orden del producto: la tira se reacomoda en los
                        // lugares que ya ocupaba, sin mover las de las otras.
                        onReordenarFotos={(ids) => aplicarOrden(reordenarDentroDelGrupo(imagenes, ids))}
                        onQuitarFoto={quitarFoto}
                        onElegirGenerada={() => setDestinoDeAlta(v.id)}
                        onSubirFoto={(archivo) => void subirFoto(archivo, v.id)}
                        onMoverFoto={moverFoto}
                        onVerFoto={abrirFoto}
                        onQuitar={() =>
                          void (async () => {
                            await quitarVariante(v.id);
                            // Sus fotos quedaron sin variante (el FK es ON DELETE
                            // SET NULL) y no se verían en ninguna tira: van a la
                            // primera que quede.
                            const queda = variantesOrdenadas.find((o) => o.id !== v.id);
                            if (producto.imagenesPorVariante && queda) {
                              await asignarSueltasAVariante(producto.id, queda.id);
                            }
                            await onCambio();
                          })()
                        }
                      />
                    );
                  })}

                  {/* Fotos que quedaron sin variante con la opción prendida. No
                      deberían existir —el alta y el borrado las acomodan— pero
                      una foto invisible se publica igual en la galería, así que
                      si aparece una tiene que poder verse y moverse. */}
                  {fotosPorVariante && fotosDeVariante(imagenes, null).length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/50 py-3 px-3">
                      <p className="text-xs font-medium text-amber-800">Sin variante</p>
                      <p className="mt-0.5 text-[11px] text-amber-700">
                        Se publican en la galería pero no son de ninguna variante. Movelas a una.
                      </p>
                      <div className="mt-2">
                        <SortableImageList
                          grande
                          imagenes={fotosDeVariante(imagenes, null).map((i) => ({
                            id: i.id,
                            orden: i.orden,
                            thumbUrl: i.thumbUrl,
                          }))}
                          onReorder={(ids) => aplicarOrden(reordenarDentroDelGrupo(imagenes, ids))}
                          onRemove={quitarFoto}
                          onVer={abrirFoto}
                          accionExtra={(id) => (
                            <MoverAVariante
                              destinos={variantesOrdenadas.map((o, j) => ({
                                id: o.id,
                                etiqueta: etiquetaDeVariante(o, j),
                              }))}
                              onMover={(v) => moverFoto(id, v)}
                            />
                          )}
                        />
                      </div>
                    </div>
                  )}

                  {fotosPorVariante && errorFoto && (
                    <p className="text-xs text-red-600">{errorFoto}</p>
                  )}
                </div>
              )}
            </Seccion>

            {/* Lo que Mercado Libre pide y Tiendanube no (ADR-2026-09-30-la-ficha-de-mercado-libre). */}
            {tiendas.includes('mercadolibre') && <SeccionMercadoLibre producto={producto} brandId={brandId ?? null} onGuardado={(datos) => onDatosMl?.(producto.id, datos)} />}

          </>
        )}
        </div>

        {/* Cómo se organiza: dónde aparece en la tienda y cómo se encuentra. Como el resto de la
            ficha: al crear y al editar lo que ya está en la tienda. */}
        {muestraLaFicha && (
          <div className="space-y-3 lg:col-start-2 lg:row-start-2">
            {/* Las categorías van ANTES de marca/etiquetas/SEO: son la navegación
                de la tienda —dónde encuentra el comprador el producto— y sin
                ninguna, el producto se publica pero no aparece en ningún lado.
                Marca y SEO son de descubrimiento, que es otra cosa y viene
                después. */}
            <Seccion
              id="categorias"
              candado={candadoDe('categorias')}
              titulo="Categorías"
              icono={<FolderTree className="w-4 h-4" />}
              abiertaPorDefecto={suyas.length > 0}
              aviso={categorias.length > 0 && suyas.length === 0 ? 'Sin categoría' : null}
              resumen={
                suyas.length === 0
                  ? 'Sin categoría'
                  : `${suyas.length} categoría${suyas.length === 1 ? '' : 's'}`
              }
            >
              {cargandoCategorias ? (
                <p className="flex items-center gap-2 text-xs text-neutral-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando…
                </p>
              ) : (
                <SelectorDeCategorias
                  categorias={categorias}
                  elegidas={suyas}
                  onCambiar={cambiarCategoria}
                  onAbrirPanel={onAbrirCategorias}
                />
              )}
              <ListaDeCambios cambios={cambiosDe('categorias')} />
              <p className="text-[11px] text-neutral-400">{ayudaDeCategorias(reglas)}</p>
            </Seccion>

            {/* Marca, etiquetas y SEO juntos: los tres son "cómo se encuentra este
                producto", y por separado eran tres secciones de dos campos. */}
            {reglas.conMarcaYSeo && (
            <Seccion
              id="seo"
              candado={candadoDe('seo')}
              titulo="Marca, etiquetas y SEO"
              icono={<Search className="w-4 h-4" />}
              abiertaPorDefecto={Boolean(
                producto.marca || producto.tags || producto.seoTitulo || producto.seoDescripcion,
              )}
              resumen={
                [producto.marca, producto.tags, producto.seoTitulo].filter(Boolean).join(' · ') ||
                'Sin completar'
              }
            >
              {/* Una columna en la barra de la derecha: a 340px, dos campos lado a lado no entran. */}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <Campo
                  etiqueta="Marca"
                  valor={producto.marca ?? ''}
                  placeholder={reglas.marcaConRespaldo ? 'Vacío usa el nombre de tu marca' : 'Opcional'}
                  ayuda={
                    reglas.marcaConRespaldo
                      ? 'VTEX la exige: si la dejás vacía, va el nombre de tu marca en Atlas.'
                      : undefined
                  }
                  {...marcaDe('marca')} onGuardar={(v) => void guardar({ marca: v })}
                />
                <Campo
                  etiqueta="Etiquetas"
                  valor={producto.tags ?? ''}
                  placeholder="verano, algodón, oferta"
                  ayuda="Separadas por coma."
                  {...marcaDe('tags')} onGuardar={(v) => void guardar({ tags: v })}
                />
              </div>
              <Campo
                etiqueta="Título SEO"
                valor={producto.seoTitulo ?? ''}
                placeholder={producto.nombre}
                maxLength={reglas.maxSeoTitulo}
                ayuda="Lo que se ve como título en Google. Vacío usa el nombre."
                {...marcaDe('seoTitulo')} onGuardar={(v) => void guardar({ seoTitulo: v })}
                accion={
                  // Un botón para los dos: la IA escribe el título y la descripción SEO juntos.
                  mejora && (
                    <BotonMejorarConIa
                      campo="seo"
                      pensando={ia.de('seo')?.estado === 'pensando'}
                      onPedir={() => ia.pedir('seo')}
                    />
                  )
                }
              />
              {mejora && (
                <PropuestaDeIa
                  estado={ia.de('seo')}
                  lineas={[
                    ...(propuestaDe('seo')?.seoTitulo ? [{ etiqueta: 'Título SEO', texto: propuestaDe('seo')!.seoTitulo! }] : []),
                    ...(propuestaDe('seo')?.seoDescripcion
                      ? [{ etiqueta: 'Descripción SEO', texto: propuestaDe('seo')!.seoDescripcion! }]
                      : []),
                  ]}
                  onUsar={() => usarPropuesta('seo')}
                  onOtra={() => ia.pedir('seo')}
                  onDescartar={() => ia.descartar('seo')}
                />
              )}
              <Campo
                etiqueta="Descripción SEO"
                multilinea
                valor={producto.seoDescripcion ?? ''}
                maxLength={reglas.maxSeoDescripcion}
                ayuda="El textito gris debajo del título en Google."
                {...marcaDe('seoDescripcion')} onGuardar={(v) => void guardar({ seoDescripcion: v })}
              />
            </Seccion>
            )}

            {/* Como en el panel de Tiendanube: Google Shopping, visibilidad y envío gratis
                (ADR-2026-10-29-visibilidad-envio-y-google-shopping). Sólo para Tiendanube. */}
            {(destinos ?? [canal]).includes('tiendanube') && (
              <>
                <Seccion
                  id="google"
                  candado={candadoDe('google')}
                  titulo="Instagram y Google Shopping"
                  icono={<ShoppingBag className="w-4 h-4" />}
                  abiertaPorDefecto={Boolean(enLaFicha.mpn || enLaFicha.rangoEdad || enLaFicha.genero)}
                  resumen={
                    [
                      enLaFicha.mpn,
                      RANGOS_DE_EDAD.find((x) => x.id === enLaFicha.rangoEdad)?.texto,
                      GENEROS.find((x) => x.id === enLaFicha.genero)?.texto,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'Sin completar'
                  }
                >
                  <p className="text-xs text-neutral-500">
                    Destacá tus productos en las vidrieras virtuales de Instagram y Google gratuitamente.
                  </p>
                  <Campo
                    etiqueta="MPN"
                    valor={enLaFicha.mpn ?? ''}
                    placeholder="Definir"
                    maxLength={70}
                    {...marcaDe('mpn')}
                    onGuardar={(v) => void guardar({ mpn: v })}
                  />
                  <a
                    href="https://support.google.com/merchants/answer/6324482?hl=es-419"
                    target="_blank"
                    rel="noreferrer"
                    className="-mt-1 inline-flex items-center gap-1 text-xs text-neutral-500 underline hover:text-brand-black"
                  >
                    Más sobre MPN <ExternalLink className="h-3 w-3" />
                  </a>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                    <SelectorDeLaTienda
                      etiqueta="Rango de edad"
                      vacio="Seleccioná el rango de edad"
                      opciones={RANGOS_DE_EDAD}
                      valor={enLaFicha.rangoEdad}
                      cambio={marcaDe('rangoEdad').cambio}
                      onElegir={(v) => void guardar({ rangoEdad: (v || '') as RangoDeEdad | '' })}
                    />
                    <SelectorDeLaTienda
                      etiqueta="Género"
                      vacio="Seleccioná el género"
                      opciones={GENEROS}
                      valor={enLaFicha.genero}
                      cambio={marcaDe('genero').cambio}
                      onElegir={(v) => void guardar({ genero: (v || '') as Genero | '' })}
                    />
                  </div>
                </Seccion>

                <Seccion
                  id="visibilidad"
                  candado={candadoDe('visibilidad')}
                  titulo="Visibilidad"
                  icono={<Eye className="w-4 h-4" />}
                  plegable={false}
                  resumen={VISIBILIDADES.find((x) => x.id === enLaFicha.visibilidad)?.texto}
                >
                  <div role="radiogroup" aria-label="Visibilidad" className="flex flex-wrap gap-2">
                    {VISIBILIDADES.map((o) => {
                      const activa = (enLaFicha.visibilidad ?? 'visible') === o.id;
                      return (
                        <button
                          key={o.id}
                          type="button"
                          role="radio"
                          aria-checked={activa}
                          onClick={() => !activa && void guardar({ visibilidad: o.id })}
                          className={`rounded-lg border py-1.5 px-3 text-sm font-medium transition-colors ${
                            activa
                              ? 'border-brand-primary bg-brand-primary text-white'
                              : 'border-neutral-300 bg-white text-brand-black hover:bg-neutral-50'
                          }`}
                        >
                          {o.texto}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-neutral-500">
                    {VISIBILIDADES.find((x) => x.id === (enLaFicha.visibilidad ?? 'visible'))?.ayuda}
                  </p>
                  <MarcaDeCambio cambio={marcaDe('visibilidad').cambio} />
                </Seccion>

                <Seccion
                  id="envioGratis"
                  candado={candadoDe('envioGratis')}
                  titulo="Envío gratis"
                  icono={<Truck className="w-4 h-4" />}
                  plegable={false}
                  resumen={enLaFicha.envioGratis ? 'Con envío gratis' : 'Sin envío gratis'}
                >
                  <label className="flex cursor-pointer items-center justify-between gap-4">
                    <span className="text-sm text-neutral-600">Ofrecé envío gratis en este producto.</span>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={enLaFicha.envioGratis === true}
                      onChange={(e) => void guardar({ envioGratis: e.target.checked })}
                      className="peer sr-only"
                    />
                    <span
                      aria-hidden="true"
                      className="relative h-6 w-11 shrink-0 rounded-full bg-neutral-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5 peer-focus-visible:ring-4 peer-focus-visible:ring-[rgb(var(--brand-primary-rgb)_/_0.2)]"
                    />
                  </label>
                  <MarcaDeCambio cambio={marcaDe('envioGratis').cambio} />
                </Seccion>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Las fotos que el producto tiene HOY en la tienda (ADR-2026-10-29-tienda-online-refleja-la-tienda). La
 * tira de abajo son las que se suman al publicar; sin esto, un producto de la tienda con fotos se veía
 * vacío. Con "Reemplazar" se apagan: son las que se borran.
 *
 * Abierto desde Tienda online (`aBorrar` no es null) cada una se borra por separado, con un "¿seguro?":
 * pasa a una fila gris que se borra en la tienda al sincronizar, y se puede deshacer hasta entonces
 * (ADR-2026-10-29-fotos-de-la-tienda-se-borran-una-por-una).
 */
function FotosEnLaTienda({
  imagenes,
  seBorran,
  aBorrar,
  onCambiarABorrar,
}: {
  imagenes: Array<{ id: number; src: string }>;
  seBorran: boolean;
  aBorrar: number[] | null;
  onCambiarABorrar: (ids: number[]) => Promise<void>;
}) {
  const [confirmando, setConfirmando] = useState<number | null>(null);
  const [guardando, setGuardando] = useState(false);
  const borradas = new Set(aBorrar ?? []);
  const quedan = imagenes.filter((im) => !borradas.has(im.id));
  const sacadas = imagenes.filter((im) => borradas.has(im.id));
  const cambiar = async (ids: number[]) => {
    setGuardando(true);
    await onCambiarABorrar(ids);
    setGuardando(false);
  };
  return (
    <div className="space-y-2">
      <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
        En tu tienda hoy · {quedan.length}
        {seBorran && <span className="ml-1.5 normal-case tracking-normal text-red-600">se borran al publicar</span>}
      </p>
      <div className="flex flex-wrap gap-2">
        {quedan.map((im) => (
          // El mismo tamaño y la misma × que las fotos nuevas (`SortableImageList` grande).
          <div key={im.id} className="relative h-28 w-28 shrink-0">
            <img
              src={im.src}
              alt=""
              loading="lazy"
              className={`h-full w-full rounded-xl object-cover shadow-sm transition-opacity ${seBorran ? 'opacity-40 grayscale' : ''}`}
            />
            {aBorrar && (
              <button
                type="button"
                onClick={() => setConfirmando(im.id)}
                disabled={guardando}
                aria-label="Borrar esta foto de la tienda"
                title="Borrar de la tienda"
                className="absolute right-1 top-1 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {quedan.length === 0 && <p className="text-xs text-neutral-400">Sin fotos en la tienda.</p>}
      </div>
      {sacadas.length > 0 && (
        <>
          <p className="pt-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
            Se borran al sincronizar · {sacadas.length}
          </p>
          <div className="flex flex-wrap gap-2">
            {sacadas.map((im) => (
              <div key={im.id} className="relative h-28 w-28 shrink-0">
                <img src={im.src} alt="" loading="lazy" className="h-full w-full rounded-xl object-cover opacity-40 shadow-sm grayscale" />
                <button
                  type="button"
                  onClick={() => void cambiar((aBorrar ?? []).filter((id) => id !== im.id))}
                  disabled={guardando}
                  className="absolute inset-x-2 bottom-2 rounded-lg bg-white/95 py-1 text-xs font-medium text-brand-primary shadow-sm hover:bg-white"
                >
                  Deshacer
                </button>
              </div>
            ))}
          </div>
        </>
      )}
      <p className="pt-1 text-[11px] font-medium uppercase tracking-wide text-neutral-400">Fotos nuevas</p>
      <ModalConfirmar
        abierto={confirmando !== null}
        titulo="¿Borrar esta foto de tu tienda?"
        detalle="Se borra de Tiendanube cuando sincronices. Hasta entonces la podés recuperar con Deshacer."
        confirmar="Borrar foto"
        trabajando={guardando}
        onCancelar={() => setConfirmando(null)}
        onConfirmar={async () => {
          if (confirmando === null) return;
          await cambiar([...(aBorrar ?? []), confirmando]);
          setConfirmando(null);
        }}
      />
    </div>
  );
}

/** Un desplegable de la ficha con la marca de "cambia en la tienda" (rango de edad, género). */
function SelectorDeLaTienda<T extends string>({
  etiqueta,
  vacio,
  opciones,
  valor,
  cambio,
  onElegir,
}: {
  etiqueta: string;
  vacio: string;
  opciones: ReadonlyArray<{ id: T; texto: string }>;
  valor: T | null;
  cambio: CambioDeCampo | null;
  onElegir: (v: T | '') => void;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-[13px] font-medium text-brand-black">{etiqueta}</span>
      <select
        value={valor ?? ''}
        onChange={(e) => onElegir(e.target.value as T | '')}
        className={`w-full rounded-xl border bg-white py-2.5 px-3 text-sm text-brand-black outline-none focus:border-brand-primary focus:ring-4 focus:ring-[rgb(var(--brand-primary-rgb)_/_0.1)] ${
          cambio ? 'border-brand-primary' : 'border-neutral-200'
        }`}
      >
        <option value="">{vacio}</option>
        {opciones.map((o) => (
          <option key={o.id} value={o.id}>
            {o.texto}
          </option>
        ))}
      </select>
      <MarcaDeCambio cambio={cambio} />
    </label>
  );
}

/** "Cambia en la tienda: antes → ahora", debajo de un control que no es un campo de texto. */
function MarcaDeCambio({ cambio }: { cambio: CambioDeCampo | null }) {
  if (!cambio) return null;
  return (
    <p className="text-[11px] text-brand-primary">
      Cambia en la tienda: {cambio.antes} → {cambio.ahora}
    </p>
  );
}

/** Lo que la ficha necesita para mostrar Fotos como está en Mercado Libre. */
export interface VistaDeMl {
  /** Los cambios de la ficha se mandan también a Mercado Libre. */
  activa: boolean;
  /** Las publicaciones del producto allá. Vacío = no está. */
  publicaciones: string[];
  conectada: boolean;
  brandId: string | null;
  onCambiar: (activa: boolean) => void;
}

/**
 * El logo de cada tienda en chico (los selectores de la ficha): el mismo de las pestañas de Tienda online,
 * la nube blanca sobre el azul y el apretón de manos sobre el amarillo.
 */
export function LogoChico({ tienda, tamano = 'h-5 w-5' }: { tienda: 'tiendanube' | 'mercadolibre'; tamano?: string }) {
  return tienda === 'tiendanube' ? (
    <span aria-hidden="true" className={`${tamano} grid shrink-0 place-items-center rounded-md bg-[#1E5BE6]`}>
      <TiendaNubeIcon className="h-[70%] w-[70%] text-white" />
    </span>
  ) : (
    <span aria-hidden="true" className={`${tamano} grid shrink-0 place-items-center overflow-hidden rounded-md bg-[#FFE600]`}>
      <img src="/images/mercado-libre.png" alt="" className="h-[95%] w-[95%] object-contain" style={{ clipPath: 'ellipse(45.5% 31.5% at 50% 50%)' }} />
    </span>
  );
}

/** Tiendanube | Mercado Libre: qué tienda se ve en Fotos. */
function SelectorDeTiendaDeFotos({
  vista,
  onCambiar,
}: {
  vista: 'tiendanube' | 'mercadolibre';
  onCambiar: (v: 'tiendanube' | 'mercadolibre') => void;
}) {
  const opciones = [
    { id: 'tiendanube' as const, texto: 'Tiendanube' },
    { id: 'mercadolibre' as const, texto: 'Mercado Libre' },
  ];
  return (
    <div role="tablist" aria-label="Ver las fotos de" className="inline-flex gap-1 rounded-full bg-[#F2F2F2] p-1">
      {opciones.map((o) => {
        const activa = vista === o.id;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={activa}
            onClick={() => onCambiar(o.id)}
            className={`flex items-center gap-1.5 rounded-full py-1.5 px-3 text-xs font-medium transition-colors ${
              activa ? 'bg-white text-brand-black shadow-sm' : 'text-black/40 hover:text-black/70'
            }`}
          >
            <LogoChico tienda={o.id} />
            {o.texto}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Fotos como están hoy en Mercado Libre (la primera publicación del producto), y si los cambios de la
 * ficha van también allá. Sin publicación, ofrece crearlo.
 */
function FotosEnMl({
  activa,
  publicaciones,
  conectada,
  brandId,
  onCambiar,
  fotosNuevas,
}: VistaDeMl & { fotosNuevas: number }) {
  const itemId = publicaciones[0] ?? null;
  const [lectura, setLectura] = useState<{ estado: 'cargando' } | { estado: 'error' } | { estado: 'listo'; fotos: Array<{ id: string; url: string }>; link: string | null }>({
    estado: 'cargando',
  });
  useEffect(() => {
    if (!itemId || !conectada) return;
    let vivo = true;
    setLectura({ estado: 'cargando' });
    describirPublicacionDeMl(brandId, itemId)
      .then((p) => vivo && setLectura({ estado: 'listo', fotos: p.imagenes ?? [], link: p.permalink }))
      .catch(() => vivo && setLectura({ estado: 'error' }));
    return () => {
      vivo = false;
    };
  }, [itemId, conectada, brandId]);

  if (!conectada) {
    return <p className="rounded-xl bg-neutral-50 py-3 px-4 text-sm text-neutral-600">Conectá tu cuenta de Mercado Libre en Marca para verlo acá.</p>;
  }
  if (!itemId) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 py-5 px-4 text-center">
        <p className="text-sm text-neutral-600">Este producto todavía no está en Mercado Libre.</p>
        <button
          type="button"
          onClick={() => onCambiar(!activa)}
          className={`mt-3 inline-flex items-center gap-2 rounded-xl py-2 px-4 text-sm font-medium transition-colors ${
            activa ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary' : 'bg-brand-primary text-white hover:bg-brand-primary-hover'
          }`}
        >
          {activa ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {activa ? 'Se crea al sincronizar' : 'Crear también en Mercado Libre'}
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">
        En Mercado Libre hoy{lectura.estado === 'listo' ? ` · ${lectura.fotos.length}` : ''}
        {lectura.estado === 'listo' && lectura.link && (
          <a href={lectura.link} target="_blank" rel="noreferrer" className="ml-2 normal-case tracking-normal text-brand-primary hover:underline">
            Ver publicación
          </a>
        )}
      </p>
      {lectura.estado === 'cargando' ? (
        <div className="flex gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 w-28 animate-pulse rounded-xl bg-neutral-100" />
          ))}
        </div>
      ) : lectura.estado === 'error' ? (
        <p className="text-sm text-red-600">No pudimos leer la publicación de Mercado Libre.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {lectura.fotos.map((f) => (
            <img key={f.id} src={f.url} alt="" loading="lazy" className="h-28 w-28 shrink-0 rounded-xl object-cover shadow-sm" />
          ))}
          {lectura.fotos.length === 0 && <p className="text-xs text-neutral-400">Sin fotos en Mercado Libre.</p>}
        </div>
      )}
      <label className="flex cursor-pointer items-center justify-between gap-4 border-t border-neutral-100 pt-3">
        <span className="text-sm text-neutral-600">
          Mandar los cambios también a Mercado Libre al sincronizar
          {fotosNuevas > 0 && activa && (
            <span className="block text-xs text-neutral-400">
              Las {fotosNuevas === 1 ? 'foto nueva se suma' : `${fotosNuevas} fotos nuevas se suman`} también allá.
            </span>
          )}
        </span>
        <input type="checkbox" role="switch" checked={activa} onChange={(e) => onCambiar(e.target.checked)} className="peer sr-only" />
        <span
          aria-hidden="true"
          className="relative h-6 w-11 shrink-0 rounded-full bg-neutral-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:shadow after:transition-transform peer-checked:bg-brand-primary peer-checked:after:translate-x-5"
        />
      </label>
    </div>
  );
}
